import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactSelect from 'react-select';
import Swal from 'sweetalert2';
import { FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { createBudget, deleteBudget, getBudgets, updateBudget } from '../api/budgetApi';
import { getCategories } from '../api/categoryApi';
import { getBanks } from '../api/bankApi';
import { createBankTransaction, deleteBankTransaction, getBankTransactions, updateBankTransaction } from '../api/bankTransactionApi';
import { getBorrowers } from '../api/borrowerApi';
import { getLenders } from '../api/lenderApi';
import {
	createBorrowerTransaction,
	deleteBorrowerTransaction,
	getBorrowerTransactionSummary,
	getBorrowerTransactions,
	updateBorrowerTransaction
} from '../api/borrowerTransactionApi';
import {
	createLenderTransaction,
	deleteLenderTransaction,
	getLenderTransactionSummary,
	getLenderTransactions,
	updateLenderTransaction
} from '../api/lenderTransactionApi';

const BudgetSection = styled.div`
	background: #fff;
	border-radius: 24px;
	padding: 32px;
	box-shadow: 0 25px 60px rgba(15, 23, 42, 0.08);
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const SectionHeader = styled.header`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
`;

const SectionIntro = styled.div`
	max-width: 540px;
	color: #475467;
`;

const ControlsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	justify-content: flex-end;
`;

const SearchInput = styled.input`
	border-radius: 999px;
	border: 1px solid #e2e8f0;
	padding: 9px 16px 9px 36px;
	font-size: 14px;
	min-width: 220px;
	background: #fff
		url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Ccircle cx="11" cy="11" r="8"/%3E%3Cpath d="m21 21-4.35-4.35"/%3E%3C/svg%3E')
		no-repeat 12px center;

	&:focus {
		outline: none;
		border-color: #6366f1;
		box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
	}
`;

const FilterSelect = styled.select`
	border-radius: 999px;
	border: 1px solid #e2e8f0;
	padding: 9px 16px;
	font-size: 14px;
	background: #fff;
`;

const DateRangeBar = styled.div`
	background: #f8fafc;
	border-radius: 20px;
	padding: 18px;
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 16px;
	border: 1px solid #e2e8f0;
`;

const RangeInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const RangeLabel = styled.span`
	font-size: 12px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #94a3b8;
`;

const RangeHint = styled.span`
	font-weight: 600;
	color: #0f172a;
`;

const RangeControls = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

const RangeInput = styled.input`
	border-radius: 12px;
	border: 1px solid #e2e8f0;
	padding: 8px 10px;
	font-size: 14px;
	background: #fff;
	min-width: 160px;

	&:focus {
		outline: none;
		border-color: #6366f1;
		box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
	}
`;

const RangeResetButton = styled.button`
	border: none;
	background: #111322;
	color: #f9fafb;
	padding: 8px 14px;
	border-radius: 999px;
	font-size: 13px;
	cursor: pointer;

	&:hover {
		background: #020617;
	}
`;

const AddButton = styled.button`
	padding: 10px 16px;
	border-radius: 999px;
	border: none;
	background: #111322;
	color: #f9fafb;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);

	&:hover {
		background: #020617;
	}
`;

const SummaryGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 16px;
`;

const SummaryCard = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 20px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const SummaryLabel = styled.span`
	font-size: 13px;
	color: #94a3b8;
	letter-spacing: 0.06em;
	text-transform: uppercase;
`;

const SummaryValue = styled.span`
	font-size: 28px;
	font-weight: 700;
	color: #0f172a;
`;

const SummaryMeta = styled.span`
	font-size: 13px;
	color: #475467;
`;


const BudgetTable = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 18px;
	overflow: hidden;
`;

const TableHead = styled.div`
	display: grid;
	grid-template-columns: 1.6fr 1fr 1fr 1fr 1.6fr 0.8fr;
	background: #f8fafc;
	padding: 14px 20px;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #94a3b8;
`;

const TableRow = styled.div`
	display: grid;
	grid-template-columns: 1.6fr 1fr 1fr 1fr 1.6fr 0.8fr;
	padding: 18px 20px;
	align-items: center;
	border-top: 1px solid #eef2ff;
	background: #ffffff;

	&:nth-child(even) {
		background: #fdfdfd;
	}
`;

const TableBody = styled.div``;

const NameCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const EntryTag = styled.span`
	align-self: flex-start;
	padding: 2px 10px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	background: rgba(14, 165, 233, 0.15);
	color: #0369a1;
`;

const BudgetName = styled.span`
	font-weight: 600;
	color: #0f172a;
`;

const PeriodText = styled.span`
	font-size: 13px;
	color: #64748b;
`;

const BudgetTypeChip = styled.span`
	align-self: flex-start;
	padding: 4px 12px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	background: rgba(79, 70, 229, 0.12);
	color: #312e81;
`;

const AmountText = styled.span`
	font-weight: 600;
	color: #0f172a;
`;

const ActionsCell = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const ActionButton = styled.button`
	padding: 6px 12px;
	border-radius: 999px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	font-size: 12px;
	color: #0f172a;
	cursor: pointer;

	&:hover {
		background: #f8fafc;
	}
`;

const DangerButton = styled.button`
	padding: 6px 12px;
	border-radius: 999px;
	border: none;
	background: #fee2e2;
	color: #b91c1c;
	font-size: 12px;
	cursor: pointer;

	&:hover {
		background: #fecaca;
	}
`;

const EmptyPlaceholder = styled.div`
	padding: 32px;
	border: 1px dashed #cbd5f5;
	border-radius: 18px;
	text-align: center;
	color: #94a3b8;
	font-size: 14px;
`;

const ModalBackdrop = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 40;
`;

const ModalCard = styled.div`
	background: #ffffff;
	border-radius: 20px;
	padding: 24px 24px 20px;
	width: 100%;
	max-width: 460px;
	box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
`;

const LargeModalCard = styled(ModalCard)`
	max-width: 920px;
`;

const ModalTitle = styled.h3`
	margin: 0 0 4px;
	font-size: 18px;
	color: #0f172a;
`;

const ModalSubtitle = styled.p`
	margin: 0 0 18px;
	font-size: 13px;
	color: #64748b;
`;

const FormField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 14px;
`;

const Label = styled.label`
	font-size: 13px;
	color: #0f172a;
	font-weight: 500;
`;

const Input = styled.input`
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	padding: 8px 10px;
	font-size: 14px;
	outline: none;

	&:focus {
		border-color: #6366f1;
		box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
	}
`;

const TextArea = styled.textarea`
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	padding: 8px 10px;
	font-size: 14px;
	resize: vertical;
	min-height: 96px;
	outline: none;

	&:focus {
		border-color: #6366f1;
		box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
	}
`;

const Select = styled.select`
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	padding: 8px 10px;
	font-size: 14px;
	background: #fff;
	outline: none;

	&:focus {
		border-color: #6366f1;
		box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
	}
`;

const RadioRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
`;

const RadioLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 14px;
	color: #0f172a;
`;

const RadioInput = styled.input`
	width: 16px;
	height: 16px;
	accent-color: #111322;
`;

const ModalActions = styled.div`
	margin-top: 10px;
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const SecondaryButton = styled.button`
	padding: 8px 14px;
	border-radius: 999px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	color: #0f172a;
	font-size: 13px;
	cursor: pointer;

	&:hover {
		background: #f8fafc;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const PrimaryButton = styled.button`
	padding: 8px 14px;
	border-radius: 999px;
	border: none;
	background: #111322;
	color: #f9fafb;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	opacity: ${(props) => (props.disabled ? 0.7 : 1)};

	&:hover {
		background: #020617;
	}

	&:disabled {
		cursor: not-allowed;
	}
`;

const ErrorText = styled.p`
	margin: 4px 0 0;
	font-size: 12px;
	color: #dc2626;
`;

const createEmptyForm = () => ({
	id: null,
	name: '',
	amount: '',
	date: '',
	category: '',
	description: ''
});

const formatDateInputValue = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const createTransactionForm = () => ({
	name: '',
	amount: '',
	bankAccountId: '',
	status: 'deposit',
	date: formatDateInputValue(new Date()),
	note: '',
	isFromCash: 'false'
});

const getDefaultDateRange = () => {
	const now = new Date();
	const endDate = new Date(now.getFullYear(), now.getMonth(), 25);
	const startDate = new Date(endDate);
	startDate.setMonth(startDate.getMonth() - 1);
	return {
		start: formatDateInputValue(startDate),
		end: formatDateInputValue(endDate)
	};
};

const parseISODate = (value) => {
	if (!value) {
		return null;
	}
	const parsed = new Date(`${value}T00:00:00`);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (value) => {
	const parsed = parseISODate(value);
	if (!parsed) {
		return '—';
	}
	return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const BudgetPage = () => {
	const [budgets, setBudgets] = useState([]);
	const [lenderTransactions, setLenderTransactions] = useState([]);
	const [borrowerTransactions, setBorrowerTransactions] = useState([]);
	const [categories, setCategories] = useState([]);
	const [banks, setBanks] = useState([]);
	const [transactions, setTransactions] = useState([]);
	const [areTransactionsLoading, setAreTransactionsLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [formState, setFormState] = useState(createEmptyForm);
	const [formError, setFormError] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
	const [transactionForm, setTransactionForm] = useState(createTransactionForm);
	const [transactionError, setTransactionError] = useState(null);
	const [isTransactionSaving, setIsTransactionSaving] = useState(false);
	const [isTransactionListOpen, setIsTransactionListOpen] = useState(false);
	const [editingTransactionId, setEditingTransactionId] = useState(null);
	const [lenders, setLenders] = useState([]);
	const [isLenderTxnModalOpen, setIsLenderTxnModalOpen] = useState(false);
	const [lenderTxnLenderId, setLenderTxnLenderId] = useState('');
	const [lenderTxnAmount, setLenderTxnAmount] = useState('');
	const [lenderTxnDate, setLenderTxnDate] = useState(() => new Date().toISOString().slice(0, 10));
	const [lenderTxnDescription, setLenderTxnDescription] = useState('');
	const [lenderTxnType, setLenderTxnType] = useState('cash');
	const [lenderTxnDirection, setLenderTxnDirection] = useState('pay');
	const [lenderTxnBankAccountId, setLenderTxnBankAccountId] = useState('');
	const [isLenderTxnSaving, setIsLenderTxnSaving] = useState(false);
	const [lenderTxnError, setLenderTxnError] = useState(null);
	const [lenderPaidTotals, setLenderPaidTotals] = useState({});
	const [isLenderSummaryOpen, setIsLenderSummaryOpen] = useState(false);
	const [lenderSummaryLenderId, setLenderSummaryLenderId] = useState('');
	const [lenderPayments, setLenderPayments] = useState([]);
	const [isLenderPaymentsLoading, setIsLenderPaymentsLoading] = useState(false);
	const [lenderPaymentsError, setLenderPaymentsError] = useState(null);
	const [editingLenderTxnId, setEditingLenderTxnId] = useState(null);
	const [borrowers, setBorrowers] = useState([]);
	const [isBorrowerTxnModalOpen, setIsBorrowerTxnModalOpen] = useState(false);
	const [borrowerTxnBorrowerId, setBorrowerTxnBorrowerId] = useState('');
	const [borrowerTxnAmount, setBorrowerTxnAmount] = useState('');
	const [borrowerTxnDate, setBorrowerTxnDate] = useState(() => new Date().toISOString().slice(0, 10));
	const [borrowerTxnDescription, setBorrowerTxnDescription] = useState('');
	const [borrowerTxnType, setBorrowerTxnType] = useState('cash');
	const [borrowerTxnDirection, setBorrowerTxnDirection] = useState('pay');
	const [borrowerTxnBankAccountId, setBorrowerTxnBankAccountId] = useState('');
	const [isBorrowerTxnSaving, setIsBorrowerTxnSaving] = useState(false);
	const [borrowerTxnError, setBorrowerTxnError] = useState(null);
	const [borrowerPaidTotals, setBorrowerPaidTotals] = useState({});
	const [isBorrowerSummaryOpen, setIsBorrowerSummaryOpen] = useState(false);
	const [borrowerSummaryBorrowerId, setBorrowerSummaryBorrowerId] = useState('');
	const [borrowerPayments, setBorrowerPayments] = useState([]);
	const [isBorrowerPaymentsLoading, setIsBorrowerPaymentsLoading] = useState(false);
	const [borrowerPaymentsError, setBorrowerPaymentsError] = useState(null);
	const [editingBorrowerTxnId, setEditingBorrowerTxnId] = useState(null);
	const [isCashSummaryOpen, setIsCashSummaryOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [categoryFilter, setCategoryFilter] = useState('All');
	const defaultRange = useMemo(() => getDefaultDateRange(), []);
	const [startDate, setStartDate] = useState(defaultRange.start);
	const [endDate, setEndDate] = useState(defaultRange.end);

	const wholeCurrencyFormatter = useMemo(
		() =>
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'LKR',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}),
		[]
	);

	const fractionalCurrencyFormatter = useMemo(
		() =>
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'LKR',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}),
		[]
	);

	const loadBudgets = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getBudgets();
			setBudgets(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching budgets:', error);
			Swal.fire({
				title: 'Load failed',
				text: error?.response?.data?.message || 'Unable to load budget entries',
				icon: 'error'
			});
		} finally {
			setIsLoading(false);
		}
	}, []);

	const loadCategories = useCallback(async () => {
		try {
			const response = await getCategories();
			setCategories(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching categories:', error);
			Swal.fire({
				title: 'Categories unavailable',
				text: error?.response?.data?.message || 'Unable to load categories. Add one in Settings.',
				icon: 'warning'
			});
		}
	}, []);

	const loadBanks = useCallback(async () => {
		try {
			const response = await getBanks();
			setBanks(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching banks:', error);
			Swal.fire({
				title: 'Banks unavailable',
				text: error?.response?.data?.message || 'Unable to load bank accounts for transactions.',
				icon: 'warning'
			});
		}
	}, []);

	const loadTransactions = useCallback(async () => {
		try {
			setAreTransactionsLoading(true);
			const response = await getBankTransactions();
			setTransactions(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching bank transactions:', error);
			Swal.fire({
				title: 'Transactions unavailable',
				text: error?.response?.data?.message || 'Unable to load bank transactions.',
				icon: 'warning'
			});
		} finally {
			setAreTransactionsLoading(false);
		}
	}, []);

	const loadLenderTransactions = useCallback(async () => {
		try {
			const response = await getLenderTransactions();
			setLenderTransactions(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching lender transactions:', error);
		}
	}, []);

	const loadBorrowerTransactions = useCallback(async () => {
		try {
			const response = await getBorrowerTransactions();
			setBorrowerTransactions(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching borrower transactions:', error);
		}
	}, []);

	const loadLenders = useCallback(async () => {
		try {
			const response = await getLenders();
			setLenders(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching lenders:', error);
		}
	}, []);

	const loadBorrowers = useCallback(async () => {
		try {
			const response = await getBorrowers();
			setBorrowers(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching borrowers:', error);
		}
	}, []);

	const loadLenderPaidTotals = useCallback(async () => {
		try {
			const response = await getLenderTransactionSummary();
			const rows = response.data?.data ?? [];
			const next = {};
			rows.forEach((row) => {
				if (row.lenderId != null) {
					const value = row.netPaid !== undefined ? row.netPaid : row.totalPaid;
					next[row.lenderId] = Number(value) || 0;
				}
			});
			setLenderPaidTotals(next);
		} catch (error) {
			console.error('Error fetching lender transaction summary:', error);
		}
	}, []);

	const loadBorrowerPaidTotals = useCallback(async () => {
		try {
			const response = await getBorrowerTransactionSummary();
			const rows = response.data?.data ?? [];
			const next = {};
			rows.forEach((row) => {
				if (row.borrowerId != null) {
					const value = row.netGiven !== undefined ? row.netGiven : row.totalGiven;
					next[row.borrowerId] = Number(value) || 0;
				}
			});
			setBorrowerPaidTotals(next);
		} catch (error) {
			console.error('Error fetching borrower transaction summary:', error);
		}
	}, []);

	const loadLenderPayments = useCallback(async (lenderId) => {
		if (!lenderId) {
			setLenderPayments([]);
			setLenderPaymentsError(null);
			return;
		}

		try {
			setIsLenderPaymentsLoading(true);
			setLenderPaymentsError(null);
			const response = await getLenderTransactions({ lenderId });
			setLenderPayments(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching lender payments:', error);
			setLenderPaymentsError(error?.response?.data?.message || 'Unable to load lender payments');
		} finally {
			setIsLenderPaymentsLoading(false);
		}
	}, []);

	const loadBorrowerPayments = useCallback(async (borrowerId) => {
		if (!borrowerId) {
			setBorrowerPayments([]);
			setBorrowerPaymentsError(null);
			return;
		}

		try {
			setIsBorrowerPaymentsLoading(true);
			setBorrowerPaymentsError(null);
			const response = await getBorrowerTransactions({ borrowerId });
			setBorrowerPayments(response.data?.data ?? []);
		} catch (error) {
			console.error('Error fetching borrower payments:', error);
			setBorrowerPaymentsError(error?.response?.data?.message || 'Unable to load borrower payments');
		} finally {
			setIsBorrowerPaymentsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadBudgets();
		loadCategories();
		loadBanks();
		loadTransactions();
		loadLenderTransactions();
		loadLenders();
		loadLenderPaidTotals();
		loadBorrowerTransactions();
		loadBorrowers();
		loadBorrowerPaidTotals();
		}, [loadBudgets, loadCategories, loadBanks, loadTransactions, loadLenderTransactions, loadLenders, loadLenderPaidTotals, loadBorrowerTransactions, loadBorrowers, loadBorrowerPaidTotals]);

	const categoryTypeMap = useMemo(() => {
		const map = new Map();
		categories.forEach((entry) => {
			const name = String(entry.name || '').trim();
			if (!name) {
				return;
			}
			const rawType = String(entry.type || '').trim();
			const normalizedType = rawType === 'Cost' ? 'Expense' : rawType || 'Expense';
			map.set(name, normalizedType);
		});
		return map;
	}, [categories]);

	const categoryOptions = useMemo(() => {
		if (categories.length > 0) {
			return categories
				.map((entry) => String(entry.name || '').trim())
				.filter((name) => name.length > 0)
				.sort((a, b) => a.localeCompare(b));
		}

		const fallback = new Set();
		budgets.forEach((entry) => {
			if (entry.category) {
				fallback.add(entry.category);
			}
		});
		return Array.from(fallback).sort((a, b) => a.localeCompare(b));
	}, [categories, budgets]);

	const bankOptions = useMemo(() => {
		return banks.map((bank) => {
			const holderName = bank.accountHolderName || bank.account_holder_name || '';
			const labelParts = [bank.name, holderName].filter(Boolean);
			const label = labelParts.join(' — ');
			const optionValue = bank.id != null ? String(bank.id) : String(bank.accountNumber || '');
			return {
				value: optionValue,
				label: label || bank.name || 'Unnamed bank'
			};
		});
	}, [banks]);

	const selectedBankOption = useMemo(() => {
		if (!transactionForm.bankAccountId) {
			return null;
		}
		return bankOptions.find((option) => option.value === String(transactionForm.bankAccountId)) || null;
	}, [bankOptions, transactionForm.bankAccountId]);

	const bankSelectStyles = useMemo(
		() => ({
			control: (base, state) => ({
				...base,
				borderRadius: 10,
				borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
				boxShadow: state.isFocused ? '0 0 0 1px rgba(99, 102, 241, 0.2)' : 'none',
				minHeight: 44,
				'&:hover': {
					borderColor: '#6366f1'
				}
			}),
			menu: (base) => ({
				...base,
				borderRadius: 12
			}),
			option: (base, state) => ({
				...base,
				backgroundColor: state.isFocused ? '#eef2ff' : '#fff',
				color: '#0f172a'
			})
		}),
		[]
	);

	const getCategoryType = useCallback(
		(name) => {
			const mapped = categoryTypeMap.get(name);
			if (!mapped) {
				return 'Expense';
			}
			return mapped === 'Cost' ? 'Expense' : mapped;
		},
		[categoryTypeMap]
	);

	const isWithinSelectedRange = useCallback(
		(value) => {
			if (!startDate && !endDate) {
				return true;
			}

			const entryDate = value ? new Date(value) : null;
			if (!entryDate || Number.isNaN(entryDate.getTime())) {
				return false;
			}

			if (startDate) {
				const start = parseISODate(startDate);
				if (start && entryDate < start) {
					return false;
				}
			}

			if (endDate) {
				const end = parseISODate(endDate);
				if (end && entryDate > end) {
					return false;
				}
			}

			return true;
		},
		[startDate, endDate]
	);

	const filteredBudgets = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return budgets.filter((entry) => {
			const matchesTerm = term
				? [entry.name, entry.category, entry.description]
						.filter(Boolean)
						.some((field) => field.toLowerCase().includes(term))
				: true;

			const matchesCategory = (() => {
				if (categoryFilter === 'All') {
					return true;
				}

				if (categoryFilter === 'Income' || categoryFilter === 'Expense') {
					return getCategoryType(entry.category) === categoryFilter;
				}

				return false;
			})();

			const matchesRange = isWithinSelectedRange(entry.date);

			return matchesTerm && matchesCategory && matchesRange;
		});
	}, [budgets, searchTerm, categoryFilter, getCategoryType, isWithinSelectedRange]);

	const transactionsInRange = useMemo(
		() => transactions.filter((txn) => isWithinSelectedRange(txn.date)),
		[transactions, isWithinSelectedRange]
	);

	const lenderTransactionsInRange = useMemo(
		() => lenderTransactions.filter((txn) => isWithinSelectedRange(txn.date)),
		[lenderTransactions, isWithinSelectedRange]
	);

	const borrowerTransactionsInRange = useMemo(
		() => borrowerTransactions.filter((txn) => isWithinSelectedRange(txn.date)),
		[borrowerTransactions, isWithinSelectedRange]
	);

	const filteredTransactions = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return transactionsInRange.filter((txn) => {
			if (!term) {
				return true;
			}

			return [txn.name, txn.note, txn.bankName, txn.accountHolderName]
				.filter(Boolean)
				.some((field) => field.toLowerCase().includes(term));
		});
	}, [transactionsInRange, searchTerm]);

	const lenderCashEntries = useMemo(() => {
		return lenderTransactionsInRange
			.filter((txn) => (txn.sourceType || 'cash') === 'cash')
			.map((txn) => ({ ...txn, entryType: 'lenderCash' }));
	}, [lenderTransactionsInRange]);

	const borrowerCashEntries = useMemo(() => {
		return borrowerTransactionsInRange
			.filter((txn) => (txn.sourceType || 'cash') === 'cash')
			.map((txn) => ({ ...txn, entryType: 'borrowerCash' }));
	}, [borrowerTransactionsInRange]);

	const combinedEntries = useMemo(() => {
		const annotatedBudgets = filteredBudgets.map((entry) => ({ ...entry, entryType: 'budget' }));
		const annotatedTransactions = filteredTransactions.map((txn) => ({ ...txn, entryType: 'transaction' }));

		const toTimestamp = (value) => {
			if (!value) {
				return 0;
			}
			const parsed = new Date(value);
			return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
		};

		return [...annotatedBudgets, ...annotatedTransactions, ...lenderCashEntries, ...borrowerCashEntries].sort((a, b) => {
			const timeA = toTimestamp(a.date);
			const timeB = toTimestamp(b.date);
			if (timeA === timeB) {
				return (a.id || 0) - (b.id || 0);
			}
			return timeA - timeB;
		});
	}, [filteredBudgets, filteredTransactions, lenderCashEntries, borrowerCashEntries]);

	const totals = useMemo(() => {
		return filteredBudgets.reduce(
			(acc, entry) => {
				acc.count += 1;
				const amount = Number(entry.amount) || 0;
				if (getCategoryType(entry.category) === 'Income') {
					acc.income += amount;
				} else {
					acc.expense += amount;
				}
				return acc;
			},
			{ count: 0, income: 0, expense: 0 }
		);
	}, [filteredBudgets, getCategoryType]);

	const cashAdjustmentParts = useMemo(() => {
		const bankImpact = transactionsInRange.reduce((total, txn) => {
			const amount = Number(txn.amount) || 0;
			if (amount === 0) {
				return total;
			}

			if (txn.status === 'deposit' && txn.isFromCash) {
				// Cash moved from tills into bank: cash decreases
				return total - amount;
			}

			if (txn.status === 'withdraw') {
				// If this withdrawal was explicitly flagged as coming
				// out as cash (bank -> cash), increase cash. Otherwise,
				// it only affects the bank balance and not cashflow.
				if (txn.isFromCash) {
					return total + amount;
				}
				return total;
			}

			return total;
		}, 0);

		const lenderCashImpact = lenderTransactionsInRange.reduce((total, txn) => {
			if (txn.sourceType !== 'cash') {
				return total;
			}
			const amount = Number(txn.amount) || 0;
			if (amount === 0) {
				return total;
			}
			const isReceive = (txn.direction || 'pay') === 'receive';
			return isReceive ? total + amount : total - amount;
		}, 0);

		const borrowerCashImpact = borrowerTransactionsInRange.reduce((total, txn) => {
			if (txn.sourceType !== 'cash') {
				return total;
			}
			const amount = Number(txn.amount) || 0;
			if (amount === 0) {
				return total;
			}
			const isReceive = (txn.direction || 'pay') === 'receive';
			return isReceive ? total + amount : total - amount;
		}, 0);

		const total = bankImpact + lenderCashImpact + borrowerCashImpact;
		return { bankImpact, lenderCashImpact, borrowerCashImpact, total };
	}, [transactionsInRange, lenderTransactionsInRange, borrowerTransactionsInRange]);

	const cashAdjustment = cashAdjustmentParts.total;

	const cashBalance = totals.income - totals.expense + cashAdjustment;
	const cashBalanceMeta = (() => {
		if (cashAdjustment === 0) {
			return 'No cash movements between banks and tills in this window.';
		}
		return cashAdjustment > 0
			? 'Withdrawals moved funds from banks into cash.'
			: 'Deposits shifted cash into bank accounts.';
	})();

	const totalBeginningBalance = useMemo(() => {
		return banks.reduce((sum, bank) => sum + (Number(bank.beginningBalance) || 0), 0);
	}, [banks]);

	const bankBalance = useMemo(() => {
		return transactions.reduce((sum, txn) => {
			const amount = Number(txn.amount) || 0;
			if (amount === 0) {
				return sum;
			}

			if (txn.status === 'deposit') {
				return sum + amount;
			}

			if (txn.status === 'withdraw') {
				return sum - amount;
			}

			return sum;
		}, totalBeginningBalance);
	}, [transactions, totalBeginningBalance]);

	const lenderBalance = useMemo(() => {
		if (!lenders.length) {
			return 0;
		}
		return lenders.reduce((sum, lender) => {
			const beginning = Number(lender.beginningAmount ?? lender.amount ?? 0) || 0;
			const net = lenderPaidTotals[lender.id] || 0; // receive - pay
			return sum + (beginning + net);
		}, 0);
	}, [lenders, lenderPaidTotals]);

	const lenderBalanceMeta = useMemo(() => {
		if (!lenders.length) {
			return 'Add lenders to see how much is owed.';
		}
		return 'Beginning balances plus net of payments made and funds received from lenders.';
	}, [lenders]);
	const borrowerBalance = useMemo(() => {
		if (!borrowers.length) {
			return 0;
		}
		return borrowers.reduce((sum, borrower) => {
			const beginning = Number(borrower.beginningAmount ?? borrower.amount ?? 0) || 0;
			const net = borrowerPaidTotals[borrower.id] || 0; // pay - receive
			return sum + (beginning + net);
		}, 0);
	}, [borrowers, borrowerPaidTotals]);

	const borrowerBalanceMeta = useMemo(() => {
		if (!borrowers.length) {
			return 'Add borrowers to see outstanding balances.';
		}
		return 'Beginning exposure plus net of disbursements and repayments.';
	}, [borrowers]);

	const formatAmount = (value) => {
		const numericValue = Number(value) || 0;
		const shouldDropDecimals = Math.abs(numericValue) > 1 && Number.isInteger(numericValue);
		const formatter = shouldDropDecimals ? wholeCurrencyFormatter : fractionalCurrencyFormatter;
		return formatter.format(numericValue);
	};

	const cashFlowEntries = useMemo(() => {
		const rows = [];

		// Budget entries: income adds cash, expenses reduce cash
		filteredBudgets.forEach((entry) => {
			const amount = Number(entry.amount) || 0;
			if (amount === 0) return;
			const isIncome = getCategoryType(entry.category) === 'Income';
			const signed = isIncome ? amount : -amount;
			rows.push({
				id: `budget-${entry.id}`,
				date: entry.date,
				source: isIncome ? 'Income' : 'Expense',
				label: entry.name || entry.category_name || entry.category || 'Budget entry',
				cashIn: signed > 0 ? signed : 0,
				cashOut: signed < 0 ? -signed : 0,
				note: entry.description || ''
			});
		});

		// Bank transactions that affect cash (same rules as cashAdjustment)
		transactionsInRange.forEach((txn) => {
			const amount = Number(txn.amount) || 0;
			if (amount === 0) return;

			let signed = 0;
			if (txn.status === 'deposit' && txn.isFromCash) {
				// Cash moved from tills into bank: cash decreases
				signed = -amount;
			} else if (txn.status === 'withdraw' && txn.isFromCash) {
				// Withdrawals marked as from cash increase tills
				signed = amount;
			} else {
				// Bank movements that are not tied to cash do not touch Balance Amount
				return;
			}

			rows.push({
				id: `bank-${txn.id}`,
				date: txn.date,
				source: 'Bank',
				label: txn.name || txn.bankName || 'Bank transaction',
				cashIn: signed > 0 ? signed : 0,
				cashOut: signed < 0 ? -signed : 0,
				note: txn.note || ''
			});
		});

		// Lender cash movements
		lenderTransactionsInRange
			.filter((txn) => (txn.sourceType || 'cash') === 'cash')
			.forEach((txn) => {
				const amount = Number(txn.amount) || 0;
				if (amount === 0) return;
				const isReceive = (txn.direction || 'pay') === 'receive';
				const signed = isReceive ? amount : -amount;
				rows.push({
					id: `lender-${txn.id}`,
					date: txn.date,
					source: 'Lender cash',
					label: txn.lenderName || txn.partyName || 'Lender',
					cashIn: signed > 0 ? signed : 0,
					cashOut: signed < 0 ? -signed : 0,
					note: txn.description || ''
				});
			});

		// Borrower cash movements
		borrowerTransactionsInRange
			.filter((txn) => (txn.sourceType || 'cash') === 'cash')
			.forEach((txn) => {
				const amount = Number(txn.amount) || 0;
				if (amount === 0) return;
				const isReceive = (txn.direction || 'pay') === 'receive';
				const signed = isReceive ? amount : -amount;
				rows.push({
					id: `borrower-${txn.id}`,
					date: txn.date,
					source: 'Borrower cash',
					label: txn.borrowerName || txn.partyName || 'Borrower',
					cashIn: signed > 0 ? signed : 0,
					cashOut: signed < 0 ? -signed : 0,
					note: txn.description || ''
				});
			});

		rows.sort((a, b) => {
			const timeA = a.date ? new Date(a.date).getTime() : 0;
			const timeB = b.date ? new Date(b.date).getTime() : 0;
			if (timeA === timeB) {
				return String(a.id).localeCompare(String(b.id));
			}
			return timeA - timeB;
		});

		return rows;
	}, [filteredBudgets, transactionsInRange, lenderTransactionsInRange, borrowerTransactionsInRange, getCategoryType]);

	const formatDate = (value) => {
		if (!value) {
			return 'No date';
		}
		const parsed = new Date(value);
		if (Number.isNaN(parsed.getTime())) {
			return value;
		}
		return parsed.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
	};

	const getCashImpactForTransaction = (txn) => {
		const amount = Number(txn.amount) || 0;
		if (amount === 0) {
			return {
				text: formatAmount(0),
				style: { color: '#0f172a' },
				icon: null
			};
		}

		if (txn.status === 'withdraw') {
			// If withdrawal is marked as coming out as cash, treat it as
			// cash inflow to tills; otherwise it only changes bank balance.
			if (txn.isFromCash) {
				return {
					text: `+${formatAmount(amount)}`,
					style: { color: '#15803d' },
					icon: null
				};
			}
			return {
				text: formatAmount(amount),
				style: { color: '#b91c1c' },
				icon: 'down'
			};
		}

		if (txn.status === 'deposit' && txn.isFromCash) {
			return {
				text: `-${formatAmount(amount)}`,
				style: { color: '#b91c1c' },
				icon: null
			};
		}

		// For deposits that are not from cash (for example, borrower
		// repayments into a bank account), they don't change Balance Amount
		// but they do increase the bank balance. Show an upward arrow icon
		// instead of a plus sign so they aren't treated as cashflow.
		if (txn.status === 'deposit') {
			return {
				text: formatAmount(amount),
				style: { color: '#15803d' },
				icon: 'up'
			};
		}

		return {
			text: formatAmount(amount),
			style: { color: '#0f172a' },
			icon: null
		};
	};

	const getBankImpactForTransaction = (txn) => {
		const amount = Number(txn.amount) || 0;
		if (amount === 0) {
			return {
				text: formatAmount(0),
				style: { color: '#0f172a' },
				icon: null
			};
		}

		if (txn.status === 'deposit') {
			return {
				text: `+${formatAmount(amount)}`,
				style: { color: '#15803d' },
				icon: 'up'
			};
		}

		if (txn.status === 'withdraw') {
			return {
				text: `-${formatAmount(amount)}`,
				style: { color: '#b91c1c' },
				icon: 'down'
			};
		}

		return {
			text: formatAmount(amount),
			style: { color: '#0f172a' },
			icon: null
		};
	};

	const getEntrySign = (entry) => {
		const rawAmount = Number(entry.amount) || 0;
		if (rawAmount === 0) {
			return 'none';
		}

		if (entry.entryType === 'budget') {
			const isIncome = getCategoryType(entry.category) === 'Income';
			return isIncome ? 'plus' : 'minus';
		}

		if (entry.entryType === 'transaction') {
			const impact = getCashImpactForTransaction(entry);
			if (typeof impact.text === 'string') {
				if (impact.text.startsWith('+')) return 'plus';
				if (impact.text.startsWith('-')) return 'minus';
			}
			return 'none';
		}

		if (entry.entryType === 'lenderCash' || entry.entryType === 'borrowerCash') {
			const isReceive = (entry.direction || 'pay') === 'receive';
			return isReceive ? 'plus' : 'minus';
		}

		return 'none';
	};

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setFormState((prev) => ({ ...prev, [name]: value }));
		setFormError(null);
	};

	const handleTransactionInputChange = (event) => {
		const { name, value } = event.target;
		setTransactionForm((prev) => ({ ...prev, [name]: value }));
		setTransactionError(null);
	};

	const handleBankSelectChange = (selectedOption) => {
		setTransactionForm((prev) => ({ ...prev, bankAccountId: selectedOption?.value ?? '' }));
		setTransactionError(null);
	};

	const handleStartDateChange = (value) => {
		setStartDate(value);
		if (endDate && value && value > endDate) {
			setEndDate(value);
		}
	};

	const handleEndDateChange = (value) => {
		setEndDate(value);
		if (startDate && value && value < startDate) {
			setStartDate(value);
		}
	};

	const handleResetRange = () => {
		setStartDate(defaultRange.start);
		setEndDate(defaultRange.end);
	};

	const handleOpenModal = () => {
		setFormState(createEmptyForm());
		setFormError(null);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		if (isSaving) {
			return;
		}
		setIsModalOpen(false);
	};

	const handleOpenTransactionModal = () => {
		setTransactionForm(createTransactionForm());
		setTransactionError(null);
		setEditingTransactionId(null);
		setIsTransactionModalOpen(true);
	};

	const handleCloseTransactionModal = () => {
		if (isTransactionSaving) {
			return;
		}
		setIsTransactionModalOpen(false);
	};

	const handleOpenTransactionList = () => {
		if (!transactions.length) {
			Swal.fire({
				title: 'No bank transactions yet',
				text: 'Capture a bank transaction first to see the history.',
				icon: 'info'
			});
			return;
		}
		setIsTransactionListOpen(true);
	};

	const handleCloseTransactionList = () => {
		setIsTransactionListOpen(false);
	};

	const handleOpenLenderTxnModal = () => {
		setLenderTxnError(null);
		setLenderTxnAmount('');
		setLenderTxnDescription('');
		setLenderTxnDate(new Date().toISOString().slice(0, 10));
		setLenderTxnType('cash');
		setLenderTxnDirection('pay');
		setLenderTxnBankAccountId('');
		if (lenders.length > 0 && !lenderTxnLenderId) {
			setLenderTxnLenderId(String(lenders[0].id));
		}
		setEditingLenderTxnId(null);
		setIsLenderTxnModalOpen(true);
	};

	const handleOpenBorrowerTxnModal = () => {
		setBorrowerTxnError(null);
		setBorrowerTxnAmount('');
		setBorrowerTxnDescription('');
		setBorrowerTxnDate(new Date().toISOString().slice(0, 10));
		setBorrowerTxnType('cash');
		setBorrowerTxnDirection('pay');
		setBorrowerTxnBankAccountId('');
		if (borrowers.length > 0 && !borrowerTxnBorrowerId) {
			setBorrowerTxnBorrowerId(String(borrowers[0].id));
		}
		setEditingBorrowerTxnId(null);
		setIsBorrowerTxnModalOpen(true);
	};

	const handleCloseLenderTxnModal = () => {
		if (isLenderTxnSaving) return;
		setIsLenderTxnModalOpen(false);
	};

	const handleCloseBorrowerTxnModal = () => {
		if (isBorrowerTxnSaving) return;
		setIsBorrowerTxnModalOpen(false);
	};

	const handleOpenLenderSummaryModal = () => {
		if (!lenders.length) {
			Swal.fire({
				title: 'No lenders yet',
				text: 'Add a lender first to see balances.',
				icon: 'info'
			});
			return;
		}

		if (!lenderSummaryLenderId) {
			setLenderSummaryLenderId(String(lenders[0].id));
		}
		const targetId = lenderSummaryLenderId || String(lenders[0].id);
		loadLenderPayments(targetId);
		setIsLenderSummaryOpen(true);
	};

	const handleCloseLenderSummaryModal = () => {
		setIsLenderSummaryOpen(false);
		setLenderPayments([]);
		setLenderPaymentsError(null);
		setEditingLenderTxnId(null);
	};

	const handleOpenBorrowerSummaryModal = () => {
		if (!borrowers.length) {
			Swal.fire({
				title: 'No borrowers yet',
				text: 'Add a borrower first to see balances.',
				icon: 'info'
			});
			return;
		}

		if (!borrowerSummaryBorrowerId) {
			setBorrowerSummaryBorrowerId(String(borrowers[0].id));
		}
		const targetId = borrowerSummaryBorrowerId || String(borrowers[0].id);
		loadBorrowerPayments(targetId);
		setIsBorrowerSummaryOpen(true);
	};

	const handleCloseBorrowerSummaryModal = () => {
		setIsBorrowerSummaryOpen(false);
		setBorrowerPayments([]);
		setBorrowerPaymentsError(null);
		setEditingBorrowerTxnId(null);
	};

	const handleBorrowerSummaryBorrowerChange = (e) => {
		const newId = e.target.value;
		setBorrowerSummaryBorrowerId(newId);
		if (newId) {
			loadBorrowerPayments(newId);
		} else {
			setBorrowerPayments([]);
			setBorrowerPaymentsError(null);
		}
	};

	const handleLenderSummaryLenderChange = (e) => {
		const newId = e.target.value;
		setLenderSummaryLenderId(newId);
		if (newId) {
			loadLenderPayments(newId);
		} else {
			setLenderPayments([]);
			setLenderPaymentsError(null);
		}
	};

	const handleStartEditLenderPayment = (payment) => {
		setEditingLenderTxnId(payment.id);
		setLenderTxnLenderId(String(payment.lenderId));
		setLenderTxnAmount(payment.amount != null ? String(payment.amount) : '');
		setLenderTxnDate(payment.date ? String(payment.date).slice(0, 10) : '');
		setLenderTxnDescription(payment.description || '');
		setLenderTxnType(payment.sourceType || 'cash');
		setLenderTxnDirection(payment.direction || 'pay');
		setLenderTxnBankAccountId(
			payment.bankAccountId != null && payment.bankAccountId !== ''
				? String(payment.bankAccountId)
				: ''
		);
		setIsLenderTxnModalOpen(true);
		setIsLenderSummaryOpen(false);
	};

	const handleDeleteLenderPayment = async (payment) => {
		const result = await Swal.fire({
			title: 'Delete payment?',
			text: 'This action cannot be undone.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, delete it',
			cancelButtonText: 'Cancel',
		});

		if (!result.isConfirmed) return;

		try {
			await deleteLenderTransaction(payment.id);
			Swal.fire('Deleted', 'Payment has been deleted.', 'success');
			if (lenderSummaryLenderId) {
				loadLenderPayments(lenderSummaryLenderId);
			}
			loadLenderPaidTotals();
			loadLenderTransactions();
		} catch (error) {
			console.error('Failed to delete lender payment', error);
			Swal.fire('Error', 'Failed to delete payment.', 'error');
		}
	};

	const handleStartEditBorrowerPayment = (payment) => {
		setEditingBorrowerTxnId(payment.id);
		setBorrowerTxnBorrowerId(String(payment.borrowerId));
		setBorrowerTxnAmount(payment.amount != null ? String(payment.amount) : '');
		setBorrowerTxnDate(payment.date ? String(payment.date).slice(0, 10) : '');
		setBorrowerTxnDescription(payment.description || '');
		setBorrowerTxnType(payment.sourceType || 'cash');
		setBorrowerTxnDirection(payment.direction || 'pay');
		setBorrowerTxnBankAccountId(
			payment.bankAccountId != null && payment.bankAccountId !== '' ? String(payment.bankAccountId) : ''
		);
		setIsBorrowerTxnModalOpen(true);
		setIsBorrowerSummaryOpen(false);
	};

	const handleDeleteBorrowerPayment = async (payment) => {
		const result = await Swal.fire({
			title: 'Delete payment?',
			text: 'This action cannot be undone.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, delete it',
			cancelButtonText: 'Cancel',
		});

		if (!result.isConfirmed) return;

		try {
			await deleteBorrowerTransaction(payment.id);
			Swal.fire('Deleted', 'Payment has been deleted.', 'success');
			if (borrowerSummaryBorrowerId) {
				loadBorrowerPayments(borrowerSummaryBorrowerId);
			}
			loadBorrowerPaidTotals();
			loadBorrowerTransactions();
		} catch (error) {
			console.error('Failed to delete borrower payment', error);
			Swal.fire('Error', 'Failed to delete payment.', 'error');
		}
	};

	const handleEditEntry = (entry) => {
		setFormState({
			id: entry.id,
			name: entry.name || '',
			amount: entry.amount !== undefined && entry.amount !== null ? String(entry.amount) : '',
			date: entry.date ? entry.date.slice(0, 10) : '',
			category: entry.category || '',
			description: entry.description || ''
		});
		setFormError(null);
		setIsModalOpen(true);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		const trimmedName = formState.name.trim();
		const trimmedCategory = formState.category.trim();
		const trimmedDescription = formState.description.trim();
		const amountValue = Number(formState.amount);

		if (!trimmedName || !formState.date || !trimmedCategory) {
			setFormError('Name, date, and category are required');
			return;
		}

		if (!Number.isFinite(amountValue) || amountValue < 0) {
			setFormError('Amount must be zero or more');
			return;
		}

		const payload = {
			name: trimmedName,
			amount: amountValue,
			date: formState.date,
			category: trimmedCategory,
			description: trimmedDescription
		};

		setIsSaving(true);
		try {
			if (formState.id) {
				await updateBudget(formState.id, payload);
			} else {
				await createBudget(payload);
			}
			await loadBudgets();
			setIsModalOpen(false);
			setFormState(createEmptyForm());
			Swal.fire({
				title: formState.id ? 'Budget updated' : 'Budget added',
				icon: 'success',
				timer: 1400,
				showConfirmButton: false
			});
		} catch (error) {
			console.error('Error saving budget entry:', error);
			setFormError(error?.response?.data?.message || 'Unable to save budget entry');
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = async (entry) => {
		const result = await Swal.fire({
			title: 'Delete entry?',
			text: `Remove ${entry.name} from the ledger?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#b91c1c',
			cancelButtonColor: '#6b7280',
			confirmButtonText: 'Delete'
		});

		if (!result.isConfirmed) {
			return;
		}

		try {
			await deleteBudget(entry.id);
			await loadBudgets();
			Swal.fire({
				title: 'Entry deleted',
				icon: 'success',
				timer: 1200,
				showConfirmButton: false
			});
		} catch (error) {
			console.error('Error deleting budget entry:', error);
			Swal.fire({
				title: 'Delete failed',
				text: error?.response?.data?.message || 'Unable to delete budget entry',
				icon: 'error'
			});
		}
	};

	const handleLenderTxnSubmit = async (event) => {
		event.preventDefault();

		const lenderIdNumeric = Number(lenderTxnLenderId);
		const amountNumeric = Number(lenderTxnAmount);

		if (!Number.isInteger(lenderIdNumeric) || lenderIdNumeric <= 0) {
			setLenderTxnError('Select a lender');
			return;
		}

		if (!Number.isFinite(amountNumeric) || amountNumeric <= 0) {
			setLenderTxnError('Amount must be greater than zero');
			return;
		}

		if (!lenderTxnDate) {
			setLenderTxnError('Date is required');
			return;
		}

		if (lenderTxnType === 'bank') {
			const bankIdNumeric = Number(lenderTxnBankAccountId);
			if (!Number.isInteger(bankIdNumeric) || bankIdNumeric <= 0) {
				setLenderTxnError('Select a valid bank when paying from bank');
				return;
			}
		}

		const createPayload = {
			lenderId: lenderIdNumeric,
			amount: amountNumeric,
			description: lenderTxnDescription.trim(),
			date: lenderTxnDate,
			sourceType: lenderTxnType,
			direction: lenderTxnDirection,
			bankAccountId: lenderTxnType === 'bank' ? Number(lenderTxnBankAccountId) : null
		};

		const updatePayload = {
			amount: amountNumeric,
			description: lenderTxnDescription.trim(),
			date: lenderTxnDate,
			sourceType: lenderTxnType,
			direction: lenderTxnDirection,
		};

		setIsLenderTxnSaving(true);
		setLenderTxnError(null);

		try {
			if (editingLenderTxnId) {
				await updateLenderTransaction(editingLenderTxnId, updatePayload);
				Swal.fire({
					title: 'Lender transaction updated',
					icon: 'success',
					timer: 1400,
					showConfirmButton: false
				});
			} else {
				await createLenderTransaction(createPayload);
				Swal.fire({
					title: 'Lender transaction recorded',
					icon: 'success',
					timer: 1400,
					showConfirmButton: false
				});
			}
			setIsLenderTxnModalOpen(false);
			setEditingLenderTxnId(null);
			await loadLenderPaidTotals();
			await loadLenderTransactions();
			// If this lender movement used a bank account, a corresponding
			// bank_transaction row was created on the backend. Refresh bank
			// transactions so balances and the cashflow table stay in sync.
			await loadTransactions();
			if (isLenderSummaryOpen && lenderSummaryLenderId) {
				await loadLenderPayments(lenderSummaryLenderId);
			}
		} catch (error) {
			console.error('Error recording lender transaction:', error);
			setLenderTxnError(error?.response?.data?.message || 'Unable to save lender transaction right now');
		} finally {
			setIsLenderTxnSaving(false);
		}
	};

	const handleBorrowerTxnSubmit = async (event) => {
		event.preventDefault();

		const borrowerIdNumeric = Number(borrowerTxnBorrowerId);
		const amountNumeric = Number(borrowerTxnAmount);

		if (!Number.isInteger(borrowerIdNumeric) || borrowerIdNumeric <= 0) {
			setBorrowerTxnError('Select a borrower');
			return;
		}

		if (!Number.isFinite(amountNumeric) || amountNumeric <= 0) {
			setBorrowerTxnError('Amount must be greater than zero');
			return;
		}

		if (!borrowerTxnDate) {
			setBorrowerTxnError('Date is required');
			return;
		}

		if (borrowerTxnType === 'bank') {
			const bankIdNumeric = Number(borrowerTxnBankAccountId);
			if (!Number.isInteger(bankIdNumeric) || bankIdNumeric <= 0) {
				setBorrowerTxnError('Select a valid bank when paying from bank');
				return;
			}
		}

		const createPayload = {
			borrowerId: borrowerIdNumeric,
			amount: amountNumeric,
			description: borrowerTxnDescription.trim(),
			date: borrowerTxnDate,
			sourceType: borrowerTxnType,
			direction: borrowerTxnDirection,
			bankAccountId: borrowerTxnType === 'bank' ? Number(borrowerTxnBankAccountId) : null
		};

		const updatePayload = {
			amount: amountNumeric,
			description: borrowerTxnDescription.trim(),
			date: borrowerTxnDate,
			sourceType: borrowerTxnType,
			direction: borrowerTxnDirection,
			bankAccountId: borrowerTxnType === 'bank' ? Number(borrowerTxnBankAccountId) : null
		};

		setIsBorrowerTxnSaving(true);
		setBorrowerTxnError(null);

		try {
			if (editingBorrowerTxnId) {
				await updateBorrowerTransaction(editingBorrowerTxnId, updatePayload);
				Swal.fire({
					title: 'Borrower transaction updated',
					icon: 'success',
					timer: 1400,
					showConfirmButton: false
				});
			} else {
				await createBorrowerTransaction(createPayload);
				Swal.fire({
					title: 'Borrower transaction recorded',
					icon: 'success',
					timer: 1400,
					showConfirmButton: false
				});
			}
			setIsBorrowerTxnModalOpen(false);
			setEditingBorrowerTxnId(null);
			await loadBorrowerPaidTotals();
			await loadBorrowerTransactions();
			// For bank-sourced borrower movements, the backend also
			// creates a bank_transaction entry. Refresh bank
			// transactions so balances and cashflow reflect this.
			await loadTransactions();
			if (isBorrowerSummaryOpen && borrowerSummaryBorrowerId) {
				await loadBorrowerPayments(borrowerSummaryBorrowerId);
			}
		} catch (error) {
			console.error('Error recording borrower transaction:', error);
			setBorrowerTxnError(error?.response?.data?.message || 'Unable to save borrower transaction right now');
		} finally {
			setIsBorrowerTxnSaving(false);
		}
	};

	const modalTitle = formState.id ? 'Edit budget entry' : 'Add budget entry';

	const handleTransactionSubmit = async (event) => {
		event.preventDefault();
		const trimmedName = transactionForm.name.trim();
		const amountValue = Number(transactionForm.amount);
		const selectedBank = transactionForm.bankAccountId;
		const numericBankId = Number(selectedBank);

		if (!trimmedName || !transactionForm.date || !selectedBank) {
			setTransactionError('Name, bank account, and date are required');
			return;
		}

		if (!Number.isFinite(amountValue) || amountValue <= 0) {
			setTransactionError('Amount must be greater than zero');
			return;
		}

		if (!Number.isInteger(numericBankId) || numericBankId <= 0) {
			setTransactionError('Select a valid bank account');
			return;
		}

		const payload = {
			name: trimmedName,
			amount: amountValue,
			bankAccountId: numericBankId,
			status: transactionForm.status,
			date: transactionForm.date,
			note: transactionForm.note.trim(),
			isFromCash: transactionForm.isFromCash === 'true'
		};

		setIsTransactionSaving(true);
		try {
			if (editingTransactionId) {
				await updateBankTransaction(editingTransactionId, payload);
			} else {
				await createBankTransaction(payload);
			}
			await loadTransactions();
			setIsTransactionModalOpen(false);
			setTransactionForm(createTransactionForm());
			Swal.fire({
				title: 'Transaction saved',
				icon: 'success',
				timer: 1400,
				showConfirmButton: false
			});
		} catch (error) {
			console.error('Error handling bank transaction:', error);
			setTransactionError(error?.response?.data?.message || 'Unable to save bank transaction right now');
		} finally {
			setIsTransactionSaving(false);
		}
	};

	const handleEditTransaction = (txn) => {
		setEditingTransactionId(txn.id);
		setTransactionForm({
			name: txn.name || '',
			amount: txn.amount !== undefined && txn.amount !== null ? String(txn.amount) : '',
			bankAccountId: txn.bankAccountId != null ? String(txn.bankAccountId) : '',
			status: txn.status || 'deposit',
			date: txn.date ? String(txn.date).slice(0, 10) : '',
			note: txn.note || '',
			isFromCash: txn.isFromCash ? 'true' : 'false'
		});
		setTransactionError(null);
		setIsTransactionListOpen(false);
		setIsTransactionModalOpen(true);
	};

	const handleDeleteTransaction = async (txn) => {
		const result = await Swal.fire({
			title: 'Delete bank transaction?',
			text: `Remove ${txn.name} from bank history?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#b91c1c',
			cancelButtonColor: '#6b7280',
			confirmButtonText: 'Delete'
		});

		if (!result.isConfirmed) {
			return;
		}

		try {
			await deleteBankTransaction(txn.id);
			await loadTransactions();
			Swal.fire({
				title: 'Transaction deleted',
				icon: 'success',
				timer: 1200,
				showConfirmButton: false
			});
		} catch (error) {
			console.error('Error deleting bank transaction:', error);
			Swal.fire({
				title: 'Delete failed',
				text: error?.response?.data?.message || 'Unable to delete bank transaction',
				icon: 'error'
			});
		}
	};

	const emptyMessage =
		searchTerm || categoryFilter !== 'All'
			? 'No budgets or bank transactions match your filters. Try adjusting search or add a new record.'
			: 'No entries yet. Log a budget or capture a bank transaction to get started.';

	const nonCashBankTransactions = combinedEntries.filter(
		(entry) => entry.entryType === 'transaction'
	);

	const incomeEntries = combinedEntries.filter((entry) => getEntrySign(entry) === 'plus');
	const expenseEntries = combinedEntries.filter((entry) => getEntrySign(entry) === 'minus');
	const otherEntries = combinedEntries.filter((entry) => {
		if (
			entry.entryType === 'transaction' &&
			(entry.status === 'deposit' || entry.status === 'withdraw') &&
			!entry.isFromCash
		) {
			return false;
		}
		return getEntrySign(entry) === 'none';
	});

	const getUnsignedAmount = (entry) => {
		return Math.abs(Number(entry.amount) || 0);
	};

	// For the bank transactions table, deposits should increase the total
	// and withdrawals should decrease it, matching the bank-side view.
	const totalNonCashBankAmount = nonCashBankTransactions.reduce((sum, entry) => {
		const amount = Number(entry.amount) || 0;
		if (entry.status === 'withdraw') {
			return sum - amount;
		}
		return sum + amount;
	}, 0);
	const totalIncomeAmount = incomeEntries.reduce((sum, entry) => sum + getUnsignedAmount(entry), 0);
	const totalExpenseAmount = expenseEntries.reduce((sum, entry) => sum + getUnsignedAmount(entry), 0);
	const totalOtherAmount = otherEntries.reduce((sum, entry) => sum + getUnsignedAmount(entry), 0);

	const overallLoading = isLoading || areTransactionsLoading;

	const renderEntryRow = (entry) => {
		const isBudgetEntry = entry.entryType === 'budget';
		const isBankTxn = entry.entryType === 'transaction';
		const isLenderCash = entry.entryType === 'lenderCash';
		const isBorrowerCash = entry.entryType === 'borrowerCash';
		const entryTagLabel = (() => {
			if (isBudgetEntry) return null;
			if (isLenderCash) return 'Lender cash';
			if (isBorrowerCash) return 'Borrower cash';
			if (isBankTxn) return 'Bank transaction';
			return null;
		})();
		const nameDisplay = (() => {
			if (isBudgetEntry) return entry.name || entry.category_name || '—';
			if (isLenderCash) return entry.lenderName || entry.partyName || entry.name || 'Lender cash';
			if (isBorrowerCash) return entry.borrowerName || entry.partyName || entry.name || 'Borrower cash';
			return entry.name || entry.bankName || 'Bank transaction';
		})();
		const secondaryLine = (() => {
			if (isBudgetEntry) {
				if (entry.updatedAt || entry.createdAt) {
					return `Updated ${formatDate(entry.updatedAt || entry.createdAt)}`;
				}
				return null;
			}

			if (isLenderCash) {
				const dirText = (entry.direction || 'pay') === 'receive' ? 'Cash inflow' : 'Cash outflow';
				const cashLabel = (entry.sourceType || 'cash') === 'cash' ? 'Cash' : entry.sourceType;
				return [dirText, cashLabel].filter(Boolean).join(' • ') || null;
			}

			if (isBorrowerCash) {
				const dirText = (entry.direction || 'pay') === 'receive' ? 'Cash inflow' : 'Cash outflow';
				const cashLabel = (entry.sourceType || 'cash') === 'cash' ? 'Cash' : entry.sourceType;
				return [dirText, cashLabel].filter(Boolean).join(' • ') || null;
			}

			const bankLabel = entry.bankName || 'Bank account';
			const holder = entry.accountHolderName;
			const combined = [bankLabel, holder].filter(Boolean).join(' • ');
			return combined || null;
		})();
		const chipLabel = (() => {
			if (isBudgetEntry) return entry.category || '—';
			if (isBankTxn) return entry.status === 'withdraw' ? 'Withdraw' : 'Deposit';
			if (isLenderCash || isBorrowerCash) {
				const isReceive = (entry.direction || 'pay') === 'receive';
				return isReceive ? 'Cash in' : 'Cash out';
			}
			return '—';
		})();
		const chipStyle = (() => {
			if (isBudgetEntry) return {};
			if (isBankTxn) {
				return entry.status === 'deposit'
					? { background: 'rgba(34, 197, 94, 0.18)', color: '#166534' }
					: { background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' };
			}
			if (isLenderCash || isBorrowerCash) {
				const isReceive = (entry.direction || 'pay') === 'receive';
				return isReceive
					? { background: 'rgba(34, 197, 94, 0.18)', color: '#166534' }
					: { background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' };
			}
			return {};
		})();
		let amountText;
		let amountStyle;
		let impactIcon = null;
		if (isBudgetEntry) {
			const isIncome = getCategoryType(entry.category) === 'Income';
			const prefix = isIncome ? '+' : '-';
			amountText = `${prefix}${formatAmount(entry.amount)}`;
			amountStyle = { color: isIncome ? '#15803d' : '#b91c1c' };
		} else if (isBankTxn) {
			const impact = getCashImpactForTransaction(entry);
			amountText = impact.text;
			amountStyle = impact.style;
			if (impact.icon === 'up') {
				impactIcon = <FiTrendingUp style={{ marginRight: 4, verticalAlign: 'middle' }} />;
			} else if (impact.icon === 'down') {
				impactIcon = <FiTrendingDown style={{ marginRight: 4, verticalAlign: 'middle' }} />;
			}
		} else if (isLenderCash) {
			const amt = Number(entry.amount) || 0;
			const isReceive = (entry.direction || 'pay') === 'receive';
			amountText = `${isReceive ? '+' : '-'}${formatAmount(Math.abs(amt))}`;
			amountStyle = { color: isReceive ? '#15803d' : '#b91c1c' };
		} else if (isBorrowerCash) {
			const amt = Number(entry.amount) || 0;
			const isReceive = (entry.direction || 'pay') === 'receive';
			amountText = `${isReceive ? '+' : '-'}${formatAmount(Math.abs(amt))}`;
			amountStyle = { color: isReceive ? '#15803d' : '#b91c1c' };
		} else {
			amountText = formatAmount(entry.amount || 0);
			amountStyle = { color: '#0f172a' };
		}

		const descriptionText = (() => {
			if (isBudgetEntry) {
				const raw = entry.description != null ? String(entry.description).trim() : '';
				return raw || '—';
			}

			if (isLenderCash) {
				const lenderLabel = entry.lenderName || 'Lender';
				const dirText = (entry.direction || 'pay') === 'receive' ? 'Received from' : 'Paid to';
				const baseDesc = entry.description && entry.description.trim().length > 0 ? entry.description.trim() : '';
				const pieces = [dirText, lenderLabel].filter(Boolean).join(' ');
				return baseDesc ? `${pieces} — ${baseDesc}` : pieces || '—';
			}

			if (isBorrowerCash) {
				const borrowerLabel = entry.borrowerName || 'Borrower';
				const dirText = (entry.direction || 'pay') === 'receive' ? 'Received from' : 'Paid to';
				const baseDesc = entry.description && entry.description.trim().length > 0 ? entry.description.trim() : '';
				const pieces = [dirText, borrowerLabel].filter(Boolean).join(' ');
				return baseDesc ? `${pieces} — ${baseDesc}` : pieces || '—';
			}

			const hasNote = entry.note != null && String(entry.note).trim().length > 0;
			let base = hasNote ? String(entry.note).trim() : '';

			if (entry.status === 'withdraw') {
				if (entry.isFromCash) {
					if (!base) {
						base = 'Cash increased from bank withdrawal (included in Balance Amount).';
					} else {
						base = `${base} (affects Balance Amount as cash increase)`;
					}
				} else {
					if (!base) {
						base = 'Cash decreased via bank payment (included in Balance Amount).';
					} else {
						base = `${base} (affects Balance Amount as cash decrease)`;
					}
				}
			} else if (entry.status === 'deposit' && entry.isFromCash) {
				if (!base) {
					base = 'Cash moved from tills into bank (reduces Balance Amount).';
				} else {
					base = `${base} (affects Balance Amount as cash decrease)`;
				}
			}

			return base || '—';
		})();

		return (
			<TableRow key={`${entry.entryType}-${entry.id}`}>
				<NameCell>
					{entryTagLabel && <EntryTag>{entryTagLabel}</EntryTag>}
					<BudgetName>{nameDisplay}</BudgetName>
					{secondaryLine && <PeriodText>{secondaryLine}</PeriodText>}
				</NameCell>
				<BudgetTypeChip style={chipStyle}>{chipLabel}</BudgetTypeChip>
				<AmountText style={amountStyle}>
					{impactIcon}
					{amountText}
				</AmountText>
				<PeriodText>{formatDate(entry.date)}</PeriodText>
				<PeriodText>{descriptionText}</PeriodText>
				<ActionsCell>
					{isBudgetEntry ? (
						<>
							<ActionButton type="button" onClick={() => handleEditEntry(entry)}>
								Edit
							</ActionButton>
							<DangerButton type="button" onClick={() => handleDelete(entry)}>
								Delete
							</DangerButton>
						</>
					) : isBankTxn ? (
						<>
							<ActionButton type="button" onClick={() => handleEditTransaction(entry)}>
								Edit
							</ActionButton>
							<DangerButton type="button" onClick={() => handleDeleteTransaction(entry)}>
								Delete
							</DangerButton>
						</>
					) : isLenderCash ? (
						<>
							<ActionButton type="button" onClick={() => handleStartEditLenderPayment(entry)}>
								Edit
							</ActionButton>
							<DangerButton type="button" onClick={() => handleDeleteLenderPayment(entry)}>
								Delete
							</DangerButton>
						</>
					) : (
						<>
							<ActionButton type="button" onClick={() => handleStartEditBorrowerPayment(entry)}>
								Edit
							</ActionButton>
							<DangerButton type="button" onClick={() => handleDeleteBorrowerPayment(entry)}>
								Delete
							</DangerButton>
						</>
					)}
				</ActionsCell>
			</TableRow>
		);
	};

	const renderBankEntryRow = (entry) => {
		const bankLabel = entry.bankName || 'Bank transaction';
		const holder = entry.accountHolderName;
		const nameDisplay = entry.name || bankLabel;
		const bankSource = [bankLabel, holder].filter(Boolean).join(' • ') || bankLabel;
		const impact = getBankImpactForTransaction(entry);
		const impactIcon = impact.icon === 'up'
			? <FiTrendingUp style={{ marginRight: 4, verticalAlign: 'middle' }} />
			: impact.icon === 'down'
				? <FiTrendingDown style={{ marginRight: 4, verticalAlign: 'middle' }} />
				: null;
		const hasNote = entry.note != null && String(entry.note).trim().length > 0;
		const descriptionText = hasNote ? String(entry.note).trim() : '—';

		return (
			<TableRow key={`bank-${entry.id}`}>
				<NameCell>
					<EntryTag>Bank transaction</EntryTag>
					<BudgetName>{nameDisplay}</BudgetName>
					<PeriodText>{bankSource}</PeriodText>
				</NameCell>
				<BudgetTypeChip style={entry.status === 'withdraw'
					? { background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' }
					: { background: 'rgba(34, 197, 94, 0.18)', color: '#166534' }}>
					{entry.status === 'withdraw' ? 'Withdraw' : 'Deposit'}
				</BudgetTypeChip>
				<AmountText style={impact.style}>
					{impactIcon}
					{impact.text}
				</AmountText>
				<PeriodText>{formatDate(entry.date)}</PeriodText>
				<PeriodText>{descriptionText}</PeriodText>
				<ActionsCell>
					<ActionButton type="button" onClick={() => handleEditTransaction(entry)}>
						Edit
					</ActionButton>
					<DangerButton type="button" onClick={() => handleDeleteTransaction(entry)}>
						Delete
					</DangerButton>
				</ActionsCell>
			</TableRow>
		);
	};

	return (
		<BudgetSection>
			<SectionHeader>
				<div>
					<h2 style={{ margin: 0, color: '#0f172a' }}>Budget ledger</h2>
					<SectionIntro>
						Track every allocation with the exact name, value, date, description, and category you need.
					</SectionIntro>
				</div>
				<ControlsRow>
					<SearchInput
						type="text"
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
						placeholder="Search by name or note"
					/>
					<FilterSelect value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
						<option value="All">All categories</option>
						<option value="Income">Income</option>
						<option value="Expense">Expenses</option>
					</FilterSelect>
					<AddButton type="button" onClick={handleOpenModal}>
						+ Add entry
					</AddButton>
					<AddButton type="button" onClick={handleOpenTransactionModal}>
						Bank Transaction
					</AddButton>
					<AddButton type="button" onClick={handleOpenBorrowerTxnModal}>
						Borrower Transaction
					</AddButton>
					<AddButton type="button" onClick={handleOpenLenderTxnModal}>
						Lender Transaction
					</AddButton>
				</ControlsRow>
			</SectionHeader>

			<DateRangeBar>
				<RangeInfo>
					<RangeLabel>Reporting window</RangeLabel>
					<RangeHint>
						{formatDisplayDate(startDate)} — {formatDisplayDate(endDate)}
					</RangeHint>
				</RangeInfo>
				<RangeControls>
					<RangeInput
						type="date"
						value={startDate}
						onChange={(event) => handleStartDateChange(event.target.value)}
					/>
					<span style={{ color: '#94a3b8', fontSize: '13px' }}>to</span>
					<RangeInput
						type="date"
						value={endDate}
						onChange={(event) => handleEndDateChange(event.target.value)}
					/>
					<RangeResetButton type="button" onClick={handleResetRange}>
						Reset range
					</RangeResetButton>
				</RangeControls>
			</DateRangeBar>

			<SummaryGrid>
				<SummaryCard>
					<SummaryLabel>Total Income</SummaryLabel>
					<SummaryValue>{formatAmount(totals.income)}</SummaryValue>
					<SummaryMeta>{totals.count} budget entries shown in this view.</SummaryMeta>
				</SummaryCard>
				<SummaryCard>
					<SummaryLabel>Total Expenses</SummaryLabel>
					<SummaryValue>{formatAmount(totals.expense)}</SummaryValue>
					<SummaryMeta>Only items categorized as expenses.</SummaryMeta>
				</SummaryCard>
				<SummaryCard onDoubleClick={() => setIsCashSummaryOpen(true)} style={{ cursor: 'pointer' }}>
					<SummaryLabel>Balance Amount</SummaryLabel>
					<SummaryValue>{formatAmount(cashBalance)}</SummaryValue>
					<SummaryMeta>{cashBalanceMeta}</SummaryMeta>
				</SummaryCard>
				<SummaryCard onDoubleClick={handleOpenTransactionList} style={{ cursor: 'pointer' }}>
					<SummaryLabel>Bank Balance</SummaryLabel>
					<SummaryValue>{formatAmount(bankBalance)}</SummaryValue>
					<SummaryMeta>Beginning balances plus every bank transaction.</SummaryMeta>
				</SummaryCard>
				<SummaryCard onDoubleClick={handleOpenLenderSummaryModal} style={{ cursor: 'pointer' }}>
					<SummaryLabel>Lender Balance</SummaryLabel>
					<SummaryValue>{formatAmount(lenderBalance)}</SummaryValue>
					<SummaryMeta>{lenderBalanceMeta}</SummaryMeta>
				</SummaryCard>
				<SummaryCard onDoubleClick={handleOpenBorrowerSummaryModal} style={{ cursor: 'pointer' }}>
					<SummaryLabel>Borrower Balance</SummaryLabel>
					<SummaryValue>{formatAmount(borrowerBalance)}</SummaryValue>
					<SummaryMeta>{borrowerBalanceMeta}</SummaryMeta>
				</SummaryCard>
				</SummaryGrid>

			{overallLoading && combinedEntries.length === 0 && (
				<EmptyPlaceholder>Loading budget and bank entries...</EmptyPlaceholder>
			)}

			{!overallLoading && combinedEntries.length === 0 && (
				<EmptyPlaceholder>{emptyMessage}</EmptyPlaceholder>
			)}

			{nonCashBankTransactions.length > 0 && (
				<>
					<h3 style={{ marginTop: 24, marginBottom: 8, color: '#0f172a', fontSize: 16 }}>
						Bank transactions (bank side)
					</h3>
					<BudgetTable>
						<TableHead>
							<span>Name</span>
							<span>Category / Source</span>
							<span>Amount</span>
							<span>Date</span>
							<span>Description / Note</span>
							<span style={{ textAlign: 'right' }}>Actions</span>
						</TableHead>
						<TableBody>
							{nonCashBankTransactions.map(renderBankEntryRow)}
							<TableRow>
								<NameCell>
									<BudgetName>Subtotal</BudgetName>
								</NameCell>
								<BudgetTypeChip style={{ opacity: 0 }}>—</BudgetTypeChip>
								<AmountText style={{ fontWeight: 700 }}>{formatAmount(totalNonCashBankAmount)}</AmountText>
								<PeriodText>—</PeriodText>
								<PeriodText>—</PeriodText>
								<ActionsCell />
							</TableRow>
						</TableBody>
					</BudgetTable>
				</>
			)}

			{incomeEntries.length > 0 && (
				<>
					<h3 style={{ marginTop: 24, marginBottom: 8, color: '#0f172a', fontSize: 16 }}>Income transactions</h3>
					<BudgetTable>
						<TableHead>
							<span>Name</span>
							<span>Category / Source</span>
							<span>Amount</span>
							<span>Date</span>
							<span>Description / Note</span>
							<span style={{ textAlign: 'right' }}>Actions</span>
						</TableHead>
						<TableBody>
							{incomeEntries.map(renderEntryRow)}
							<TableRow>
								<NameCell>
									<BudgetName>Subtotal</BudgetName>
								</NameCell>
								<BudgetTypeChip style={{ opacity: 0 }}>—</BudgetTypeChip>
								<AmountText style={{ fontWeight: 700 }}>{formatAmount(totalIncomeAmount)}</AmountText>
								<PeriodText>—</PeriodText>
								<PeriodText>—</PeriodText>
								<ActionsCell />
							</TableRow>
						</TableBody>
					</BudgetTable>
				</>
			)}

			{expenseEntries.length > 0 && (
				<>
					<h3 style={{ marginTop: 24, marginBottom: 8, color: '#0f172a', fontSize: 16 }}>Expense transactions</h3>
					<BudgetTable>
						<TableHead>
							<span>Name</span>
							<span>Category / Source</span>
							<span>Amount</span>
							<span>Date</span>
							<span>Description / Note</span>
							<span style={{ textAlign: 'right' }}>Actions</span>
						</TableHead>
						<TableBody>
							{expenseEntries.map(renderEntryRow)}
							<TableRow>
								<NameCell>
									<BudgetName>Subtotal</BudgetName>
								</NameCell>
								<BudgetTypeChip style={{ opacity: 0 }}>—</BudgetTypeChip>
								<AmountText style={{ fontWeight: 700 }}>{formatAmount(totalExpenseAmount)}</AmountText>
								<PeriodText>—</PeriodText>
								<PeriodText>—</PeriodText>
								<ActionsCell />
							</TableRow>
						</TableBody>
					</BudgetTable>
				</>
			)}

			{otherEntries.length > 0 && (
				<>
					<h3 style={{ marginTop: 24, marginBottom: 8, color: '#0f172a', fontSize: 16 }}>Other transactions</h3>
					<BudgetTable>
						<TableHead>
							<span>Name</span>
							<span>Category / Source</span>
							<span>Amount</span>
							<span>Date</span>
							<span>Description / Note</span>
							<span style={{ textAlign: 'right' }}>Actions</span>
						</TableHead>
						<TableBody>
							{otherEntries.map(renderEntryRow)}
							<TableRow>
								<NameCell>
									<BudgetName>Subtotal</BudgetName>
								</NameCell>
								<BudgetTypeChip style={{ opacity: 0 }}>—</BudgetTypeChip>
								<AmountText style={{ fontWeight: 700 }}>{formatAmount(totalOtherAmount)}</AmountText>
								<PeriodText>—</PeriodText>
								<PeriodText>—</PeriodText>
								<ActionsCell />
							</TableRow>
						</TableBody>
					</BudgetTable>
				</>
			)}

			{isModalOpen && (
				<ModalBackdrop onClick={handleCloseModal}>
					<ModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>{modalTitle}</ModalTitle>
						<ModalSubtitle>
							Store the basics: the who, how much, when, and why of every allocation.
						</ModalSubtitle>

						<form onSubmit={handleSubmit}>
							<FormField>
								<Label htmlFor="budget-name">Name</Label>
								<Input
									id="budget-name"
									name="name"
									type="text"
									value={formState.name}
									onChange={handleInputChange}
									placeholder="e.g. Marketing push"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="budget-amount">Amount (LKR)</Label>
								<Input
									id="budget-amount"
									name="amount"
									type="number"
									step="0.01"
									min="0"
									value={formState.amount}
									onChange={handleInputChange}
									placeholder="0.00"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="budget-date">Date</Label>
								<Input
									id="budget-date"
									name="date"
									type="date"
									value={formState.date}
									onChange={handleInputChange}
								/>
							</FormField>

							<FormField>
								<Label htmlFor="budget-category">Category</Label>
								<Select
									id="budget-category"
									name="category"
									value={formState.category}
									onChange={handleInputChange}
									disabled={categoryOptions.length === 0}
								>
									<option value="">Select a category</option>
									{categoryOptions.map((category) => (
										<option key={category} value={category}>
											{category}
										</option>
									))}
								</Select>
								{categoryOptions.length === 0 && (
									<PeriodText style={{ color: '#b91c1c' }}>
										Add categories first so they appear here.
									</PeriodText>
								)}
							</FormField>

							<FormField>
								<Label htmlFor="budget-description">Description</Label>
								<TextArea
									id="budget-description"
									name="description"
									value={formState.description}
									onChange={handleInputChange}
									placeholder="Optional details or notes"
								/>
							</FormField>

							{formError && <ErrorText>{formError}</ErrorText>}

							<ModalActions>
								<SecondaryButton type="button" onClick={handleCloseModal} disabled={isSaving}>
									Cancel
								</SecondaryButton>
								<PrimaryButton type="submit" disabled={isSaving}>
									{isSaving ? 'Saving...' : formState.id ? 'Update entry' : 'Save entry'}
								</PrimaryButton>
							</ModalActions>
						</form>
					</ModalCard>
				</ModalBackdrop>
			)}

					{isCashSummaryOpen && (
						<ModalBackdrop onClick={() => setIsCashSummaryOpen(false)}>
							<LargeModalCard onClick={(event) => event.stopPropagation()}>
								<ModalTitle>Balance Amount cashflow</ModalTitle>
								<ModalSubtitle>
									Every line here either adds to or reduces the Balance Amount for this reporting window.
								</ModalSubtitle>

								<div style={{ maxHeight: 360, overflow: 'auto', marginBottom: 8 }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
										<thead>
											<tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
												<th style={{ padding: '6px 4px' }}>Date</th>
												<th style={{ padding: '6px 4px' }}>Source</th>
												<th style={{ padding: '6px 4px' }}>Label</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Cash in</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Cash out</th>
												<th style={{ padding: '6px 4px' }}>Note</th>
											</tr>
										</thead>
										<tbody>
											{cashFlowEntries.length === 0 && (
												<tr>
													<td colSpan={6} style={{ padding: '10px 4px', color: '#64748b', fontSize: 13 }}>
														No cash movements in this reporting window.
													</td>
												</tr>
											)}
											{cashFlowEntries.map((row) => {
												const net = row.cashIn - row.cashOut;
												return (
													<tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
														<td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>
															{row.date ? formatDate(row.date) : '—'}
														</td>
														<td style={{ padding: '6px 4px' }}>{row.source}</td>
														<td style={{ padding: '6px 4px' }}>{row.label}</td>
														<td style={{ padding: '6px 4px', textAlign: 'right', color: '#15803d', whiteSpace: 'nowrap' }}>
															{row.cashIn ? `+${formatAmount(row.cashIn)}` : '—'}
														</td>
														<td style={{ padding: '6px 4px', textAlign: 'right', color: '#b91c1c', whiteSpace: 'nowrap' }}>
															{row.cashOut ? `-${formatAmount(row.cashOut)}` : '—'}
														</td>
														<td style={{ padding: '6px 4px' }}>{row.note || '—'}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>

								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginTop: 8,
										fontSize: 13
									}}
								>
									<span style={{ color: '#64748b' }}>Total Balance Amount impact</span>
									<span style={{ fontWeight: 600, color: cashBalance >= 0 ? '#15803d' : '#b91c1c' }}>
										{formatAmount(cashBalance)}
									</span>
								</div>

								<ModalActions>
									<SecondaryButton type="button" onClick={() => setIsCashSummaryOpen(false)}>
										Close
									</SecondaryButton>
								</ModalActions>
							</LargeModalCard>
						</ModalBackdrop>
					)}

			{isTransactionModalOpen && (
				<ModalBackdrop onClick={handleCloseTransactionModal}>
					<ModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>Manage bank transaction</ModalTitle>
						<ModalSubtitle>Log a deposit or withdrawal and decide if it moves cash.</ModalSubtitle>

						<form onSubmit={handleTransactionSubmit}>
							<FormField>
								<Label htmlFor="transaction-name">Name</Label>
								<Input
									id="transaction-name"
									name="name"
									type="text"
									value={transactionForm.name}
									onChange={handleTransactionInputChange}
									placeholder="e.g. Utility settlement"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="transaction-amount">Amount</Label>
								<Input
									id="transaction-amount"
									name="amount"
									type="number"
									min="0"
									step="0.01"
									value={transactionForm.amount}
									onChange={handleTransactionInputChange}
									placeholder="0.00"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="transaction-bank">Bank account</Label>
								<ReactSelect
									inputId="transaction-bank"
									classNamePrefix="bank-select"
									placeholder="Select bank and holder"
									isDisabled={banks.length === 0}
									options={bankOptions}
									value={selectedBankOption}
									onChange={handleBankSelectChange}
									isClearable
									styles={bankSelectStyles}
								/>
								{banks.length === 0 && (
									<PeriodText style={{ color: '#b91c1c' }}>
										Add a bank account first to connect transactions.
									</PeriodText>
								)}
							</FormField>

							<FormField>
								<Label>Transaction type</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="status"
											value="deposit"
											checked={transactionForm.status === 'deposit'}
											onChange={handleTransactionInputChange}
										/>
										Deposit
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="status"
											value="withdraw"
											checked={transactionForm.status === 'withdraw'}
											onChange={handleTransactionInputChange}
										/>
										Withdraw
									</RadioLabel>
								</RadioRow>
							</FormField>

							<FormField>
								<Label htmlFor="transaction-date">Date</Label>
								<Input
									id="transaction-date"
									name="date"
									type="date"
									value={transactionForm.date}
									onChange={handleTransactionInputChange}
								/>
							</FormField>

							<FormField>
								<Label htmlFor="transaction-note">Note</Label>
								<TextArea
									id="transaction-note"
									name="note"
									value={transactionForm.note}
									onChange={handleTransactionInputChange}
									placeholder="Optional note"
								/>
							</FormField>

							<FormField>
								<Label>Is from cash?</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="isFromCash"
											value="true"
											checked={transactionForm.isFromCash === 'true'}
											onChange={handleTransactionInputChange}
										/>
										Yes
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="isFromCash"
											value="false"
											checked={transactionForm.isFromCash === 'false'}
											onChange={handleTransactionInputChange}
										/>
										No
									</RadioLabel>
								</RadioRow>
							</FormField>

							{transactionError && <ErrorText>{transactionError}</ErrorText>}

							<ModalActions>
								<SecondaryButton type="button" onClick={handleCloseTransactionModal} disabled={isTransactionSaving}>
									Cancel
								</SecondaryButton>
								<PrimaryButton type="submit" disabled={isTransactionSaving}>
									{isTransactionSaving ? 'Saving...' : 'Save transaction'}
								</PrimaryButton>
							</ModalActions>
						</form>
					</ModalCard>
				</ModalBackdrop>
			)}

			{isBorrowerTxnModalOpen && (
				<ModalBackdrop onClick={handleCloseBorrowerTxnModal}>
					<ModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>Borrower transaction</ModalTitle>
						<ModalSubtitle>Record disbursing to or receiving from a borrower via cash or bank.</ModalSubtitle>

						<form onSubmit={handleBorrowerTxnSubmit}>
							<FormField>
								<Label htmlFor="borrower-txn-borrower">Borrower</Label>
								<select
									id="borrower-txn-borrower"
									value={borrowerTxnBorrowerId}
									onChange={(event) => setBorrowerTxnBorrowerId(event.target.value)}
									style={{
										borderRadius: 10,
										border: '1px solid #e2e8f0',
										padding: '8px 10px',
										fontSize: 14
									}}
								>
									<option value="">Select borrower</option>
									{borrowers.map((borrower) => (
										<option key={borrower.id} value={borrower.id}>
											{borrower.name}
										</option>
									))}
								</select>
							</FormField>

							{(() => {
								const borrower = borrowers.find((entry) => String(entry.id) === String(borrowerTxnBorrowerId));
								if (!borrower) return null;
								const beginning = Number(borrower.beginningAmount ?? borrower.amount ?? 0) || 0;
								const net = borrowerPaidTotals[borrower.id] || 0; // pay - receive
								const balance = beginning + net;
								return (
									<PeriodText style={{ marginTop: 4 }}>
										Current balance to receive: <strong>{formatAmount(balance)}</strong>
									</PeriodText>
								);
							})()}

							<FormField>
								<Label htmlFor="borrower-txn-amount">Amount</Label>
								<Input
									id="borrower-txn-amount"
									type="number"
									step="0.01"
									value={borrowerTxnAmount}
									onChange={(event) => setBorrowerTxnAmount(event.target.value)}
									placeholder="0.00"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="borrower-txn-date">Date</Label>
								<Input
									id="borrower-txn-date"
									type="date"
									value={borrowerTxnDate}
									onChange={(event) => setBorrowerTxnDate(event.target.value)}
								/>
							</FormField>

							<FormField>
								<Label>Type</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="borrower-txn-type"
											value="cash"
											checked={borrowerTxnType === 'cash'}
											onChange={() => setBorrowerTxnType('cash')}
										/>
										Cash
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="borrower-txn-type"
											value="bank"
											checked={borrowerTxnType === 'bank'}
											onChange={() => setBorrowerTxnType('bank')}
										/>
										Bank
									</RadioLabel>
								</RadioRow>
							</FormField>

							<FormField>
								<Label>Flow</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="borrower-txn-direction"
											value="pay"
											checked={borrowerTxnDirection === 'pay'}
											onChange={() => setBorrowerTxnDirection('pay')}
										/>
										Pay borrower
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="borrower-txn-direction"
											value="receive"
											checked={borrowerTxnDirection === 'receive'}
											onChange={() => setBorrowerTxnDirection('receive')}
										/>
										Receive from borrower
									</RadioLabel>
								</RadioRow>
							</FormField>

							{borrowerTxnType === 'bank' && (
								<FormField>
									<Label htmlFor="borrower-txn-bank">Bank account</Label>
									<ReactSelect
										inputId="borrower-txn-bank"
										classNamePrefix="bank-select"
										placeholder="Select bank and holder"
										isDisabled={banks.length === 0}
										options={bankOptions}
										value={bankOptions.find((option) => option.value === String(borrowerTxnBankAccountId)) || null}
										onChange={(option) => setBorrowerTxnBankAccountId(option?.value ?? '')}
										isClearable
										styles={bankSelectStyles}
									/>
									{banks.length === 0 && (
										<PeriodText style={{ color: '#b91c1c' }}>
											Add a bank account first to connect transactions.
										</PeriodText>
									)}
								</FormField>
							)}

							<FormField>
								<Label htmlFor="borrower-txn-description">Description</Label>
								<TextArea
									id="borrower-txn-description"
									name="description"
									value={borrowerTxnDescription}
									onChange={(event) => setBorrowerTxnDescription(event.target.value)}
									placeholder="Optional details or notes"
								/>
							</FormField>

							{borrowerTxnError && <ErrorText>{borrowerTxnError}</ErrorText>}

							<ModalActions>
								<SecondaryButton type="button" onClick={handleCloseBorrowerTxnModal} disabled={isBorrowerTxnSaving}>
									Cancel
								</SecondaryButton>
								<PrimaryButton type="submit" disabled={isBorrowerTxnSaving}>
									{isBorrowerTxnSaving
										? 'Saving...'
										: editingBorrowerTxnId
										? 'Update transaction'
										: 'Save transaction'}
								</PrimaryButton>
							</ModalActions>
						</form>
					</ModalCard>
				</ModalBackdrop>
			)}

			{isLenderTxnModalOpen && (
				<ModalBackdrop onClick={handleCloseLenderTxnModal}>
					<ModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>Lender transaction</ModalTitle>
						<ModalSubtitle>Record paying a lender or receiving funds from them via cash or bank.</ModalSubtitle>

						<form onSubmit={handleLenderTxnSubmit}>
							<FormField>
								<Label htmlFor="lender-txn-lender">Lender</Label>
								<select
									id="lender-txn-lender"
									value={lenderTxnLenderId}
									onChange={(event) => setLenderTxnLenderId(event.target.value)}
									style={{
										borderRadius: 10,
										border: '1px solid #e2e8f0',
										padding: '8px 10px',
										fontSize: 14
									}}
								>
									<option value="">Select lender</option>
									{lenders.map((lender) => (
										<option key={lender.id} value={lender.id}>
											{lender.name}
										</option>
									))}
								</select>
							</FormField>

							{(() => {
								const selectedLender = lenders.find((l) => String(l.id) === String(lenderTxnLenderId));
								if (!selectedLender) return null;
								const beginning = Number(selectedLender.beginningAmount ?? selectedLender.amount ?? 0) || 0;
								const net = lenderPaidTotals[selectedLender.id] || 0; // receive - pay
								const balance = beginning + net;
								return (
									<PeriodText style={{ marginTop: 4 }}>
										Current balance to pay: <strong>{formatAmount(balance)}</strong>
									</PeriodText>
								);
							})()}

							<FormField>
								<Label htmlFor="lender-txn-amount">Amount</Label>
								<Input
									id="lender-txn-amount"
									type="number"
									step="0.01"
									value={lenderTxnAmount}
									onChange={(event) => setLenderTxnAmount(event.target.value)}
									placeholder="0.00"
								/>
							</FormField>

							<FormField>
								<Label htmlFor="lender-txn-date">Date</Label>
								<Input
									id="lender-txn-date"
									type="date"
									value={lenderTxnDate}
									onChange={(event) => setLenderTxnDate(event.target.value)}
								/>
							</FormField>

							<FormField>
								<Label>Type</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="lender-txn-type"
											value="cash"
											checked={lenderTxnType === 'cash'}
											onChange={() => setLenderTxnType('cash')}
										/>
										Cash
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="lender-txn-type"
											value="bank"
											checked={lenderTxnType === 'bank'}
											onChange={() => setLenderTxnType('bank')}
										/>
										Bank
									</RadioLabel>
								</RadioRow>
							</FormField>

							<FormField>
								<Label>Flow</Label>
								<RadioRow>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="lender-txn-direction"
											value="pay"
											checked={lenderTxnDirection === 'pay'}
											onChange={() => setLenderTxnDirection('pay')}
										/>
										Pay lender
									</RadioLabel>
									<RadioLabel>
										<RadioInput
											type="radio"
											name="lender-txn-direction"
											value="receive"
											checked={lenderTxnDirection === 'receive'}
											onChange={() => setLenderTxnDirection('receive')}
										/>
										Receive from lender
									</RadioLabel>
								</RadioRow>
							</FormField>

							{lenderTxnType === 'bank' && (
								<FormField>
									<Label htmlFor="lender-txn-bank">Bank account</Label>
									<ReactSelect
										inputId="lender-txn-bank"
										classNamePrefix="bank-select"
										placeholder="Select bank and holder"
										isDisabled={banks.length === 0}
										options={bankOptions}
										value={bankOptions.find((option) => option.value === String(lenderTxnBankAccountId)) || null}
										onChange={(option) => setLenderTxnBankAccountId(option?.value ?? '')}
										isClearable
										styles={bankSelectStyles}
									/>
									{banks.length === 0 && (
										<PeriodText style={{ color: '#b91c1c' }}>
											Add a bank account first to connect transactions.
										</PeriodText>
									)}
								</FormField>
							)}

							<FormField>
								<Label htmlFor="lender-txn-description">Description</Label>
								<TextArea
									id="lender-txn-description"
									name="description"
									value={lenderTxnDescription}
									onChange={(event) => setLenderTxnDescription(event.target.value)}
									placeholder="Optional details or notes"
								/>
							</FormField>

							{lenderTxnError && <ErrorText>{lenderTxnError}</ErrorText>}

							<ModalActions>
								<SecondaryButton type="button" onClick={handleCloseLenderTxnModal} disabled={isLenderTxnSaving}>
									Cancel
								</SecondaryButton>
								<PrimaryButton type="submit" disabled={isLenderTxnSaving}>
									{isLenderTxnSaving
										? 'Saving...'
										: editingLenderTxnId
										? 'Update transaction'
										: 'Save transaction'}
								</PrimaryButton>
							</ModalActions>
						</form>
					</ModalCard>
				</ModalBackdrop>
			)}

			{isLenderSummaryOpen && (
				<ModalBackdrop onClick={handleCloseLenderSummaryModal}>
					<LargeModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>Lender balances</ModalTitle>
						<ModalSubtitle>
							Select a lender to see beginning, paid, and current balance, and manage individual payments.
						</ModalSubtitle>

						<FormField>
							<Label htmlFor="lender-summary-lender">Lender</Label>
							<select
								id="lender-summary-lender"
								value={lenderSummaryLenderId}
								onChange={handleLenderSummaryLenderChange}
								style={{
									borderRadius: 10,
									border: '1px solid #e2e8f0',
									padding: '8px 10px',
									fontSize: 14
								}}
							>
								<option value="">Select lender</option>
								{lenders.map((lender) => (
									<option key={lender.id} value={lender.id}>
										{lender.name}
									</option>
								))}
							</select>
						</FormField>

						{(() => {
							const lender = lenders.find((entry) => String(entry.id) === String(lenderSummaryLenderId));
							if (!lender) {
								return null;
							}
							const beginning = Number(lender.beginningAmount ?? lender.amount ?? 0) || 0;
							const net = lenderPaidTotals[lender.id] || 0; // receive - pay
							const balance = beginning + net;
							return (
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
										gap: '12px',
										marginBottom: 8
									}}
								>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Beginning balance
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(beginning)}
										</p>
									</div>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Net received / paid
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(net)}
										</p>
									</div>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Current balance to pay
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(balance)}
										</p>
									</div>
								</div>
							);
						})()}

						<div>
							<h4 style={{ margin: '12px 0 8px', fontSize: 14, color: '#0f172a' }}>Lender payments</h4>
							{isLenderPaymentsLoading && <p style={{ fontSize: 13 }}>Loading payments...</p>}
							{lenderPaymentsError && !isLenderPaymentsLoading && (
								<p style={{ color: '#b91c1c', fontSize: 13 }}>{lenderPaymentsError}</p>
							)}
							{!isLenderPaymentsLoading && !lenderPaymentsError && lenderPayments.length === 0 && (
								<p style={{ fontSize: 13, color: '#64748b' }}>No payments recorded for this lender.</p>
							)}
							{!isLenderPaymentsLoading && !lenderPaymentsError && lenderPayments.length > 0 && (
								<div style={{ maxHeight: 300, overflow: 'auto', marginTop: 4 }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
										<thead>
											<tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
												<th style={{ padding: '6px 4px' }}>Date</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
												<th style={{ padding: '6px 4px' }}>Flow</th>
												<th style={{ padding: '6px 4px' }}>Type</th>
												<th style={{ padding: '6px 4px' }}>Bank</th>
												<th style={{ padding: '6px 4px' }}>Description</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{lenderPayments.map((payment) => {
												const bankLabel = (() => {
													const holder = payment.accountHolderName || payment.account_holder_name || '';
													const combined = [payment.bankName, holder].filter(Boolean).join(' — ');
													if (combined) return combined;
													if ((payment.sourceType || '').toLowerCase() === 'cash') return 'Cash';
													return '—';
												})();

												return (
													<tr key={payment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
														<td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>
															{payment.date ? String(payment.date).slice(0, 10) : ''}
														</td>
														{(() => {
															const amt = Number(payment.amount) || 0;
															const isReceive = payment.direction === 'receive';
															const prefix = isReceive ? '+' : '-';
															const color = isReceive ? '#15803d' : '#b91c1c';
															return (
																<td style={{ padding: '6px 4px', whiteSpace: 'nowrap', textAlign: 'right', color }}>
																	{`${prefix}${formatAmount(Math.abs(amt))}`}
																</td>
															);
														})()}
														<td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>
															{payment.direction || ''}
														</td>
														<td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>
															{payment.sourceType || ''}
														</td>
														<td style={{ padding: '6px 4px' }}>{bankLabel}</td>
														<td style={{ padding: '6px 4px' }}>
															{payment.description || ''}
														</td>
														<td style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
															<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
																<ActionButton type="button" onClick={() => handleStartEditLenderPayment(payment)}>
																	Edit
																</ActionButton>
																<DangerButton type="button" onClick={() => handleDeleteLenderPayment(payment)}>
																	Delete
																</DangerButton>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>

						<ModalActions>
							<SecondaryButton type="button" onClick={handleCloseLenderSummaryModal}>
								Close
							</SecondaryButton>
						</ModalActions>
					</LargeModalCard>
				</ModalBackdrop>
			)}

			{isBorrowerSummaryOpen && (
				<ModalBackdrop onClick={handleCloseBorrowerSummaryModal}>
					<LargeModalCard onClick={(event) => event.stopPropagation()}>
						<ModalTitle>Borrower balances</ModalTitle>
						<ModalSubtitle>
							Select a borrower to see beginning, net movements, and current balance, and manage individual disbursements or repayments.
						</ModalSubtitle>

						<FormField>
							<Label htmlFor="borrower-summary-borrower">Borrower</Label>
							<select
								id="borrower-summary-borrower"
								value={borrowerSummaryBorrowerId}
								onChange={handleBorrowerSummaryBorrowerChange}
								style={{
									borderRadius: 10,
									border: '1px solid #e2e8f0',
									padding: '8px 10px',
									fontSize: 14
								}}
							>
								<option value="">Select borrower</option>
								{borrowers.map((borrower) => (
									<option key={borrower.id} value={borrower.id}>
										{borrower.name}
									</option>
								))}
							</select>
						</FormField>

						{(() => {
							const borrower = borrowers.find((entry) => String(entry.id) === String(borrowerSummaryBorrowerId));
							if (!borrower) {
								return null;
							}
							const beginning = Number(borrower.beginningAmount ?? borrower.amount ?? 0) || 0;
							const net = borrowerPaidTotals[borrower.id] || 0; // pay - receive
							const balance = beginning + net;
							return (
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
										gap: '12px',
										marginBottom: 8
									}}
								>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Beginning balance
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(beginning)}
										</p>
									</div>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Net disbursed / received
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(net)}
										</p>
									</div>
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
										<p style={{ margin: 0, fontSize: 12, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
											Current balance to receive
										</p>
										<p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
											{formatAmount(balance)}
										</p>
									</div>
								</div>
							);
						})()}

						<div>
							<h4 style={{ margin: '12px 0 8px', fontSize: 14, color: '#0f172a' }}>Borrower movements</h4>
							{isBorrowerPaymentsLoading && <p style={{ fontSize: 13 }}>Loading payments...</p>}
							{borrowerPaymentsError && !isBorrowerPaymentsLoading && (
								<p style={{ color: '#b91c1c', fontSize: 13 }}>{borrowerPaymentsError}</p>
							)}
							{!isBorrowerPaymentsLoading && !borrowerPaymentsError && borrowerPayments.length === 0 && (
								<p style={{ fontSize: 13, color: '#64748b' }}>No movements recorded for this borrower.</p>
							)}
							{!isBorrowerPaymentsLoading && !borrowerPaymentsError && borrowerPayments.length > 0 && (
								<div style={{ maxHeight: 300, overflow: 'auto', marginTop: 4 }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
										<thead>
											<tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
												<th style={{ padding: '6px 4px' }}>Date</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
												<th style={{ padding: '6px 4px' }}>Flow</th>
												<th style={{ padding: '6px 4px' }}>Type</th>
												<th style={{ padding: '6px 4px' }}>Bank</th>
												<th style={{ padding: '6px 4px' }}>Description</th>
												<th style={{ padding: '6px 4px', textAlign: 'right' }}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{borrowerPayments.map((payment) => {
												const bankLabel = (() => {
													const holder = payment.accountHolderName || payment.account_holder_name || '';
													const combined = [payment.bankName, holder].filter(Boolean).join(' — ');
													if (combined) return combined;
													if ((payment.sourceType || '').toLowerCase() === 'cash') return 'Cash';
													return '—';
												})();

												return (
													<tr key={payment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
														<td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>
															{payment.date ? String(payment.date).slice(0, 10) : ''}
														</td>
														{(() => {
															const amt = Number(payment.amount) || 0;
															const isReceive = payment.direction === 'receive';
															const prefix = isReceive ? '+' : '-';
															const color = isReceive ? '#15803d' : '#b91c1c';
															return (
																<td style={{ padding: '6px 4px', whiteSpace: 'nowrap', textAlign: 'right', color }}>
																	{`${prefix}${formatAmount(Math.abs(amt))}`}
																</td>
															);
														})()}
														<td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>
															{payment.direction || ''}
														</td>
														<td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>
															{payment.sourceType || ''}
														</td>
														<td style={{ padding: '6px 4px' }}>{bankLabel}</td>
														<td style={{ padding: '6px 4px' }}>
															{payment.description || ''}
														</td>
														<td style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
															<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
																<ActionButton type="button" onClick={() => handleStartEditBorrowerPayment(payment)}>
																	Edit
																</ActionButton>
																<DangerButton type="button" onClick={() => handleDeleteBorrowerPayment(payment)}>
																	Delete
																</DangerButton>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>

						<ModalActions>
							<SecondaryButton type="button" onClick={handleCloseBorrowerSummaryModal}>
								Close
							</SecondaryButton>
						</ModalActions>
					</LargeModalCard>
				</ModalBackdrop>
			)}

			{isTransactionListOpen && (
				<ModalBackdrop onClick={handleCloseTransactionList}>
					<LargeModalCard onClick={(event) => event.stopPropagation()} style={{ maxWidth: 1040 }}>
						<ModalTitle>Bank transactions</ModalTitle>
						<ModalSubtitle>Review every bank movement from newest date to oldest.</ModalSubtitle>

						<div style={{ marginBottom: 8, fontSize: 13, color: '#64748b' }}>
							<span>Beginning balance (all bank accounts): </span>
							<strong style={{ color: '#0f172a' }}>{formatAmount(totalBeginningBalance)}</strong>
						</div>

						<div style={{ maxHeight: 360, overflow: 'auto', marginBottom: 8 }}>
							<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
								<thead>
									<tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
										<th style={{ padding: '6px 4px' }}>Date</th>
										<th style={{ padding: '6px 4px' }}>Bank / Holder</th>
										<th style={{ padding: '6px 4px' }}>Name</th>
										<th style={{ padding: '6px 4px' }}>Amount</th>
										<th style={{ padding: '6px 4px' }}>Type</th>
										<th style={{ padding: '6px 4px' }}>Cash?</th>
										<th style={{ padding: '6px 4px' }}>Note</th>
										<th style={{ padding: '6px 4px', textAlign: 'right' }}>Actions</th>
									</tr>
								</thead>
								<tbody>
									{[...transactions]
										.sort((a, b) => {
											const timeA = a.date ? new Date(a.date).getTime() : 0;
											const timeB = b.date ? new Date(b.date).getTime() : 0;
											if (timeA === timeB) {
												return (b.id || 0) - (a.id || 0);
											}
											return timeB - timeA;
										})
										.map((txn) => {
												const bankLabel = [txn.bankName, txn.accountHolderName]
													.filter(Boolean)
													.join(' — ') || 'Bank account';
												const impact = getBankImpactForTransaction(txn);
												const impactIcon = impact.icon === 'up'
													? <FiTrendingUp style={{ marginRight: 4, verticalAlign: 'middle' }} />
													: impact.icon === 'down'
														? <FiTrendingDown style={{ marginRight: 4, verticalAlign: 'middle' }} />
														: null;
											return (
												<tr key={txn.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
													<td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatDate(txn.date)}</td>
													<td style={{ padding: '6px 4px' }}>{bankLabel}</td>
													<td style={{ padding: '6px 4px' }}>{txn.name}</td>
														<td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color: impact.style.color }}>
															{impactIcon}
															{impact.text}
														</td>
													<td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>{txn.status}</td>
													<td style={{ padding: '6px 4px' }}>{txn.isFromCash ? 'Yes' : 'No'}</td>
													<td style={{ padding: '6px 4px' }}>{txn.note || '—'}</td>
													<td style={{ padding: '6px 4px', textAlign: 'right' }}>
														<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
															<ActionButton type="button" onClick={() => handleEditTransaction(txn)}>
																Edit
															</ActionButton>
															<DangerButton type="button" onClick={() => handleDeleteTransaction(txn)}>
																Delete
															</DangerButton>
														</div>
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>

						<ModalActions>
							<SecondaryButton type="button" onClick={handleCloseTransactionList}>
								Close
							</SecondaryButton>
						</ModalActions>
					</LargeModalCard>
				</ModalBackdrop>
			)}
		</BudgetSection>
	);
};

export default BudgetPage;
